/* eslint-disable no-unused-vars, no-await-in-loop */
import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
// Shared multi-practice helpers — promoted out of this file in phase-1
// of the All Practices rollout so lead tables, widgets, campaigns, etc.
// can use the same `practice__in` suffix pattern.
import { toPracticeIds, practiceInSuffix } from 'src/utils/practiceInSuffix';
import { normalizeLandingUrl } from 'src/utils/normalizeLandingUrl';
import { updateError } from './user';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

// Parse API dates safely — backend mixes ISO ("2024-12-18T17:08:37Z") and
// DD-MM-YYYY with microseconds + Z ("18-12-2024 17:08:37.194995Z"). Two
// gotchas in dayjs:
//   1) The `Z` token in a customParseFormat string is treated as a UTC
//      offset parser, not a literal — so `DD-MM-YYYY HH:mm:ss.SSSZ` does
//      NOT match "...216Z". This was causing every archived lead to be
//      dropped and the popup to show 0 when the widget said N.
//   2) customParseFormat only handles up to 3 fractional digits (SSS).
// Fix: strip the trailing Z + anything past millisecond precision, then
// parse as UTC explicitly (utc plugin) so we get the right epoch ms.
const API_DATE_FORMATS_UTC = [
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
    'DD-MM-YYYY HH:mm:ss.SSS',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY',
    'DD/MM/YYYY',
];
// Keep the exported name for other modules (popup components read dates via
// the slice's helpers) — these formats are only reached when no UTC suffix.
const API_DATE_FORMATS = API_DATE_FORMATS_UTC;
export const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr !== 'string') {
        const d = dayjs(dateStr);
        return d.isValid() ? d : null;
    }
    // Strip everything that customParseFormat can't grok: trailing Z,
    // timezone offsets like "+00:00", and microseconds beyond milliseconds.
    const trimmed = dateStr
        .replace(/\.(\d{3})\d+/, '.$1')
        .replace(/Z$/, '')
        .replace(/[+-]\d{2}:?\d{2}$/, '');
    // Treat as UTC — backend always serialises to UTC. We iterate the format
    // list ourselves instead of passing an array to dayjs.utc() because
    // dayjs has a bug where passing an array + strict + a format containing
    // `.SSS` falls back to local-time parsing, producing an off-by-TZ-offset
    // result. Single-string format parse avoids the bug.
    const matched = API_DATE_FORMATS_UTC
        .map((fmt) => dayjs.utc(trimmed, fmt, true))
        .find((d) => d.isValid());
    if (matched) return matched;
    const fallback = dayjs.utc(trimmed);
    if (fallback.isValid()) return fallback;
    const localFallback = dayjs(dateStr);
    return localFallback.isValid() ? localFallback : null;
};

// Valid archive reasons — MUST mirror backend `ARCHIVE_REASON_NOTES` in
// apps/reports/views/archived_lead_summary.py. Keep this list sorted
// alphabetically so drift is easy to spot in review.
//
// Why this matters: this set drives three behaviours:
//   1. Filtering the raw /reports/leads/?group_by=lead_notes__note rows
//      into just archive-related reasons (line ~251, ~1015 below).
//   2. Picking the archive reason off a lead's lead_notes[] array in
//      byReason breakdowns and Lost Leads detail (line ~3132, ~3160).
//   3. Matching the UI dropdown the user can filter by.
//
// Drift symptom: if the backend group_by returns a reason this set
// doesn't include, the frontend drops the rows silently — the widget
// total disagrees with the sum of the by_reason buckets. The phase-5
// review caught exactly that: backend added NOT_INTERESTED, DUPLICATE,
// and SPAM; frontend kept FOLLOW_UP (which backend deliberately excludes
// because it's a follow-up note type, not an archive reason).
export const VALID_ARCHIVE_REASONS = [
    'ANOTHER_PRACTICE',
    'APPOINTMENT_RELATED',
    'AWAITING_RESPONSE',
    'CHANGED_MIND',
    'CLINICALLY_NOT_SUITABLE',
    'DUPLICATE',
    'DUPLICATE_ENTRY',
    'NHS_WAITING_LIST',
    'NO_RESPONSE',
    'NOT_INTERESTED',
    'OTHER',
    'PRACTICE_TOO_FAR',
    'SPAM',
    'TOO_EXPENSIVE',
    'TREATMENT_RELATED',
    'UNABLE_TO_CONTACT',
    'WANT_NHS',
];

// Helper to format archive reason label
const formatArchiveReasonLabel = (reason) => {
    if (reason === 'WANT_NHS') return 'Want NHS';
    if (reason === 'NHS_WAITING_LIST') return 'NHS Waiting List';
    return reason
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
};

// Backend limitation (verified 2026-04-17 against staging):
//   - /archived/ caps page size at 50 and silently ignores every date filter we tested
//     (archived_at__gte/lte/range/gt/lt, created_at__gte/lte, *_after/*_before).
//   - /reports/leads/?is_archived=true only filters by created_at, which is semantically
//     wrong for "Lost in period" (a lead created in Jan but archived in Apr belongs to the
//     April Lost Leads report, not January's).
//   - /reports/archived-lead-summary/?time_after=X&time_before=Y DOES respect archived_at
//     and returns an accurate count, but no per-lead detail.
// Strategy: use the summary endpoint for counts, and paginate /archived/ ordered by
// archived_at desc for per-lead data, breaking early once we pass dateRange.start.
// maxPages caps the paginated fetch — default 40 pages (2000 leads) is plenty for
// building byReason/byTreatment/bySource breakdowns. The detailed Lost Leads page
// and popups can opt in to more via { maxPages }. KPI counts should come from
// fetchArchivedCountForPeriod (summary endpoint) so they stay accurate regardless
// of this cap.
//
// NOTE: no early-termination on "hit an older-than-range lead". The backend
// silently ignores `ordering=-archived_at` and returns 1000+ rows sharing a
// placeholder date ("15-08-2024 08:24:24.311744Z") first, so breaking early
// would drop real in-range leads. Instead we fetch up to `maxPages` and filter
// client-side — slower but correct. Exported so the popup can share the
// same helper and keep counts consistent across widget / popup / breakdown.
export const fetchArchivedLeadsInPeriod = async (practiceId, dateRange, { maxPages = 40, reasonIn = null, onProgress = null } = {}) => {
    if (!practiceId || !dateRange?.start || !dateRange?.end) return [];
    const startMs = dayjs(dateRange.start, API_DATE_FORMATS).valueOf();
    const endMs = dayjs(dateRange.end, API_DATE_FORMATS).valueOf();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return [];
    const reasonFilter = reasonIn ? `&archived_reason__in=${encodeURIComponent(reasonIn)}` : '';
    const pageSize = 50;

    // Send archived_at filter + ordering directly on the URL. If the backend
    // supports them (deployed from feature_reporting_api_improvements), the
    // first page already contains ONLY in-range rows ordered newest-first and
    // we short-circuit. If the backend still silently ignores them, we
    // fall back to parallel pagination + client-side filter.
    const startDateOnly = String(dateRange.start).split(' ')[0];
    const endDateOnly = String(dateRange.end).split(' ')[0];
    const dateFilter = `&archived_at__gte=${startDateOnly}&archived_at__lte=${endDateOnly}`;
    const ordering = '&ordering=-archived_at';
    const buildUrl = (offset) =>
        `/api/v1/practices/${practiceId}/archived/?is_archived=true&limit=${pageSize}&offset=${offset}${reasonFilter}${dateFilter}${ordering}`;

    const filterInRange = (rows) => rows.filter((lead) => {
        const parsed = parseApiDate(lead.archived_at);
        if (!parsed) return false;
        const ms = parsed.valueOf();
        return ms >= startMs && ms <= endMs;
    });

    // Page 1: learn totalCount AND detect whether backend honoured the filter.
    const first = await callApi(buildUrl(0));
    const firstResults = first?.data?.results || [];
    const totalCount = first?.data?.count ?? 0;
    const backendFiltered = firstResults.length > 0
        && filterInRange(firstResults).length === firstResults.length
        && totalCount <= firstResults.length * 2; // a tight count means server filtered, not that we happened to hit a good page
    const inRange = filterInRange(firstResults);

    if (typeof onProgress === 'function') {
        onProgress({ loaded: firstResults.length, total: backendFiltered ? totalCount : Math.min(totalCount, maxPages * pageSize), kept: inRange.length });
    }
    // Server did the work → nothing left to scan.
    if (backendFiltered) return inRange;
    if (firstResults.length < pageSize) return inRange;

    // Backend ignored the filter. Build the list of offsets we still need and
    // fetch them in parallel batches (6-wide) — same pattern as the main
    // /leads/ popup uses. Sequential pagination across 5000 rows is ~30s;
    // 6-wide parallel makes it ~5s.
    const cap = Math.min(totalCount, maxPages * pageSize);
    const remainingOffsets = [];
    for (let off = pageSize; off < cap; off += pageSize) remainingOffsets.push(off);
    const CONCURRENCY = 6;
    let loaded = firstResults.length;
    for (let i = 0; i < remainingOffsets.length; i += CONCURRENCY) {
        const batch = remainingOffsets.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        const pages = await Promise.all(batch.map((off) => callApi(buildUrl(off))));
        const batchRows = pages.flatMap((page) => page?.data?.results || []);
        loaded += batchRows.length;
        inRange.push(...filterInRange(batchRows));
        if (typeof onProgress === 'function') {
            onProgress({ loaded: Math.min(loaded, cap), total: cap, kept: inRange.length });
        }
    }
    return inRange;
};

// Multi-practice helpers (toPracticeIds, practiceInSuffix) moved to
// src/utils/practiceInSuffix.js in phase-1 of the All Practices rollout
// — see the import near the top of this file.

// Fetch accurate archived count for a date range via the summary endpoint.
// Accepts either a single practice id or an array; when multiple are given,
// the backend's `practice__in` aggregates across them in a single request.
// Returns 0 on any error so callers can fall back gracefully.
const fetchArchivedCountForPeriod = async (practiceIdOrIds, dateRange) => {
    if (!dateRange?.start || !dateRange?.end) return 0;
    const ids = toPracticeIds(practiceIdOrIds);
    const primary = ids[0];
    if (!primary) return 0;
    const startDate = String(dateRange.start).split(' ')[0];
    const endDate = String(dateRange.end).split(' ')[0];
    const res = await callApi(
        `/api/v1/practices/${primary}/reports/archived-lead-summary/?time_after=${startDate}&time_before=${endDate}${practiceInSuffix(ids)}`
    );
    return res?.data?.count ?? 0;
};

/**
 * Fetch archived lead count AND per-reason breakdown from the summary
 * endpoint in one round-trip. When the backend supports
 * `?breakdown=reason` (feature_reporting_phase_2), both pieces come from
 * the same archived_at-filtered queryset so the widget total and per-
 * reason sums always agree. When the backend doesn't yet support it, the
 * response is missing `by_reason` and the caller falls back to the
 * legacy group_by query for the breakdown.
 *
 * Returns { count, by_reason } where by_reason is an array of
 * { reason, label, count } sorted descending by count, or null when the
 * backend didn't supply it.
 */
const fetchArchivedSummaryWithBreakdown = async (practiceIdOrIds, dateRange) => {
    if (!dateRange?.start || !dateRange?.end) return { count: 0, by_reason: null };
    const ids = toPracticeIds(practiceIdOrIds);
    const primary = ids[0];
    if (!primary) return { count: 0, by_reason: null };
    const startDate = String(dateRange.start).split(' ')[0];
    const endDate = String(dateRange.end).split(' ')[0];
    try {
        const res = await callApi(
            `/api/v1/practices/${primary}/reports/archived-lead-summary/?time_after=${startDate}&time_before=${endDate}&breakdown=reason${practiceInSuffix(ids)}`
        );
        const count = res?.data?.count ?? 0;
        const raw = res?.data?.by_reason;
        if (!Array.isArray(raw)) return { count, byReason: null };
        const VALID = new Set(VALID_ARCHIVE_REASONS);
        const byReason = raw
            .filter((row) => row && VALID.has(row.reason))
            .map((row) => ({
                reason: row.reason,
                label: formatArchiveReasonLabel(row.reason),
                count: row.count || 0,
            }))
            .sort((a, b) => b.count - a.count);
        return { count, byReason };
    } catch {
        return { count: 0, byReason: null };
    }
};

// Note: fanOutAndAggregate / mergeGroupedCounts / mergeTrend were removed in
// phase 5 cleanup. Every thunk that used to fan out across practices now uses
// the backend's `?practice__in=1,2,3` aggregation, so the client-side fold
// helpers are no longer reachable. See analyticsReports phase commits for the
// thunk-by-thunk migration.

const INITIAL_STATE = {
    isLoading: false,
    error: null,
    // Cache keys to prevent redundant re-fetches when navigating between tabs
    _overviewFetchKey: null,
    _clinicianFetchKey: null,
    _treatmentFetchKey: null,
    _tcoFetchKey: null,
    _sourceFetchKey: null,
    _referralFetchKey: null,
    _landingUrlFetchKey: null,
    dateRange: {
        preset: 'last30days',
        startDate: null,
        endDate: null,
    },
    // Practice override for analytics tabs (null/empty = use global practice from header).
    // Array so analytics can aggregate across multiple practices for multi-practice users.
    // Hydrated from localStorage so the selection survives reload — wrapped in
    // a try/catch because access fails in some private-mode browsers.
    reportPracticeIds: (() => {
        try {
            const raw = localStorage.getItem('reportPracticeIds');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return null;
            const ints = parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
            return ints.length ? ints : null;
        } catch {
            return null;
        }
    })(),
    // Overview data
    kpis: {
        totalLeads: { value: 0, previousValue: 0 },
        consultationsBooked: { value: 0, previousValue: 0 },
        attended: { value: 0, previousValue: 0 },
        treatmentStarted: { value: 0, previousValue: 0 },
        lostLeads: { value: 0, previousValue: 0 },
    },
    funnel: [],
    treatmentBreakdown: [],
    topPractitioners: [],
    monthlyTrend: [],

    // Archived leads data
    archivedLeads: {
        total: 0,
        byReason: [], // [{ reason: string, label: string, count: number, leadIds: [] }]
    },

    // Lead popup state
    leadPopup: {
        open: false,
        practiceId: null,
        leadIds: [],
        title: '',
    },

    // Overview source/referral data
    overviewSources: [],
    overviewReferrals: [],

    // By Treatment data
    selectedTreatment: null,
    treatmentTotalLeads: 0, // ungrouped unique lead count for KPI (avoids double-counting multi-treatment leads)
    treatmentList: [],
    treatmentKpis: {},
    treatmentFunnel: [],
    treatmentMonthly: [],
    treatmentPractitioners: [],

    // Monthly enquiries by treatment
    monthlyEnquiries: {
        data: [],
        isLoading: false,
        selectedTreatmentId: null,
    },

    // Treatment status summary (all treatments with status breakdown)
    treatmentStatusSummary: {
        data: [],
        isLoading: false,
    },

    // Treatment funnel via state_transition_params (consistent with Overview funnel methodology)
    treatmentFunnelData: null,

    // By Practitioner data
    practitioners: [],
    practitionersPage: 1,
    practitionersSortBy: 'revenue',
    practitionersSearch: '',
    // Enhanced practitioner stats with date filtering
    practitionerStats: {
        isLoading: false,
        // Leads assigned to each practitioner in the date range
        leadsByPractitioner: [],
        // Consultation performance per practitioner
        consultationsByPractitioner: [],
        // Treatment performance per practitioner
        treatmentsByPractitioner: [],
        // Team averages for comparison
        teamAverages: {
            avgLeads: 0,
            avgConsultations: 0,
            avgConversions: 0,
            avgRevenue: 0,
            avgConversionRate: 0,
            avgShowUpRate: 0,
        },
    },

    // By Source data
    sources: [],
    campaigns: [],
    sourceInsights: {},

    // Source status breakdown (for By Source page)
    sourceStatusData: {
        data: [],
        isLoading: false,
    },
    referralStatusData: {
        data: [],
        isLoading: false,
    },
    // Landing-page URL breakdown — buckets leads by their `entire_url`
    // metadata (set at form submit). entire_url can live in either
    // lead.lead_metadata[].value (key='entire_url') or
    // lead.lead_source.metadata.entire_url; the thunk checks both.
    // Leads with no entire_url fall under a synthetic "(no landing URL)" row.
    // `progress` tracks paginated-fetch state so the UI can render
    // "Loading N of M leads..." rather than a featureless spinner during
    // the worst-case 5000-lead scan.
    landingUrlData: {
        data: [],
        isLoading: false,
        progress: { loaded: 0, total: 0 },
    },

    // Trends data
    selectedMetric: 'leads',
    trendData: [],
    yoyData: [],
    seasonality: [],
    forecast: [],
    milestones: [],

    // WhatsApp data
    whatsapp: {
        isLoading: false,
        totalConversations: 0,
        activeConversations: 0,
        closedConversations: 0,
        avgResponseTime: 0,          // in minutes
        within24hr: 0,               // percentage
        closeReasons: [],            // [{ reason, label, count, threadIds }]
        responseTimeDistribution: [], // [{ bucket, count, label }]
        leadConversion: {
            totalWhatsAppLeads: 0,
            consultationsBooked: 0,
            treatmentStarted: 0,
        },
    },

    // Clinician Conversion Tracker data
    clinicianConversion: {
        isLoading: false,
        data: [], // Processed practitioner conversion data
        treatments: [], // List of treatments for column headers
        teamTotals: {}, // Aggregated totals across all practitioners
    },

    // TCO/User Performance Tracker data
    tcoPerformance: {
        isLoading: false,
        data: [], // Processed TCO/User performance data
        teamTotals: {}, // Aggregated totals
    },

    // Lost Leads data
    lostLeads: {
        isLoading: false,
        kpis: {
            totalLost: 0,
            topReason: '',
            avgDaysToArchive: 0,
            lostRevenue: 0,
            archiveRate: 0,
            momChange: null,
        },
        byReason: [],
        byTreatment: [],
        bySource: [],
        byTeamMember: [],
        trend: [],
        detailedLeads: [],
    },

    // Click Tracking data (phone + WhatsApp click events from practice website)
    clickTracking: {
        events: [],
        kpis: { totalClicks: 0, phoneClicks: 0, whatsappClicks: 0, topPage: null, topSource: null, topConvertingPage: null },
        // Per-page form-submission aggregate from ONLINE_FORM leads in the
        // selected date range. Lives at the page level — NOT joined onto
        // every click event. Each row is { pagePath, pageLabel, submissions,
        // clicks, conversionRate }. Consumed by the standalone
        // FormSubmissionsByPageCard, not by the click events table.
        formSubmissionsByPage: [],
        isLoading: false,
        error: null,
        _fetchKey: '',
    },
};

const slice = createSlice({
    name: 'analyticsReports',
    initialState: INITIAL_STATE,
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setFetchKey: (state, action) => {
            const { key, value } = action.payload;
            state[key] = value;
        },
        // Null all fetch-dedup keys. Used by error-recovery flows so the
        // next effect pass re-dispatches the thunk without having to wait
        // for the user to change a filter / date preset.
        clearFetchKeys: (state) => {
            state._overviewFetchKey = null;
            state._clinicianFetchKey = null;
            state._treatmentFetchKey = null;
            state._tcoFetchKey = null;
            state._sourceFetchKey = null;
            state._referralFetchKey = null;
            state._landingUrlFetchKey = null;
            state.clickTracking._fetchKey = '';
            state.clickTracking.error = null;
            state.error = null;
        },
        setDateRange: (state, action) => {
            state.dateRange = action.payload;
        },
        setKpis: (state, action) => {
            state.kpis = action.payload;
        },
        setFunnel: (state, action) => {
            state.funnel = action.payload;
        },
        setTreatmentBreakdown: (state, action) => {
            state.treatmentBreakdown = action.payload;
        },
        setTopPractitioners: (state, action) => {
            state.topPractitioners = action.payload;
        },
        setMonthlyTrend: (state, action) => {
            state.monthlyTrend = action.payload;
        },
        setSelectedTreatment: (state, action) => {
            state.selectedTreatment = action.payload;
        },
        setTreatmentTotalLeads: (state, action) => {
            state.treatmentTotalLeads = action.payload;
        },
        setTreatmentList: (state, action) => {
            state.treatmentList = action.payload;
        },
        setTreatmentKpis: (state, action) => {
            state.treatmentKpis = action.payload;
        },
        setTreatmentFunnel: (state, action) => {
            state.treatmentFunnel = action.payload;
        },
        setTreatmentMonthly: (state, action) => {
            state.treatmentMonthly = action.payload;
        },
        setTreatmentPractitioners: (state, action) => {
            state.treatmentPractitioners = action.payload;
        },
        setMonthlyEnquiries: (state, action) => {
            state.monthlyEnquiries = { ...state.monthlyEnquiries, ...action.payload };
        },
        setMonthlyEnquiriesLoading: (state, action) => {
            state.monthlyEnquiries.isLoading = action.payload;
        },
        setTreatmentStatusSummary: (state, action) => {
            state.treatmentStatusSummary = { ...state.treatmentStatusSummary, ...action.payload };
        },
        setTreatmentStatusSummaryLoading: (state, action) => {
            state.treatmentStatusSummary.isLoading = action.payload;
        },
        setTreatmentFunnelData: (state, action) => {
            state.treatmentFunnelData = action.payload;
        },
        setReportPracticeIds: (state, action) => {
            const {payload} = action;
            let next;
            if (payload === null || payload === undefined) {
                next = null;
            } else if (Array.isArray(payload)) {
                next = payload.length ? payload : null;
            } else {
                next = [payload];
            }
            state.reportPracticeIds = next;
            // Persist the selection so it survives page reloads. Keyed
            // generically — the hook initialises from this on mount.
            try {
                if (next === null) localStorage.removeItem('reportPracticeIds');
                else localStorage.setItem('reportPracticeIds', JSON.stringify(next));
            } catch {
                // ignore quota errors / private-mode
            }
        },
        setPractitioners: (state, action) => {
            state.practitioners = action.payload;
        },
        setPractitionersPage: (state, action) => {
            state.practitionersPage = action.payload;
        },
        setPractitionersSortBy: (state, action) => {
            state.practitionersSortBy = action.payload;
        },
        setPractitionersSearch: (state, action) => {
            state.practitionersSearch = action.payload;
        },
        setPractitionerStats: (state, action) => {
            state.practitionerStats = { ...state.practitionerStats, ...action.payload };
        },
        setPractitionerStatsLoading: (state, action) => {
            state.practitionerStats.isLoading = action.payload;
        },
        setSources: (state, action) => {
            state.sources = action.payload;
        },
        setCampaigns: (state, action) => {
            state.campaigns = action.payload;
        },
        setSourceInsights: (state, action) => {
            state.sourceInsights = action.payload;
        },
        setSelectedMetric: (state, action) => {
            state.selectedMetric = action.payload;
        },
        setTrendData: (state, action) => {
            state.trendData = action.payload;
        },
        setYoyData: (state, action) => {
            state.yoyData = action.payload;
        },
        setSeasonality: (state, action) => {
            state.seasonality = action.payload;
        },
        setForecast: (state, action) => {
            state.forecast = action.payload;
        },
        setMilestones: (state, action) => {
            state.milestones = action.payload;
        },
        setArchivedLeads: (state, action) => {
            state.archivedLeads = action.payload;
        },
        setLeadPopup: (state, action) => {
            state.leadPopup = action.payload;
        },
        setOverviewSources: (state, action) => {
            state.overviewSources = action.payload;
        },
        setOverviewReferrals: (state, action) => {
            state.overviewReferrals = action.payload;
        },
        setWhatsAppData: (state, action) => {
            state.whatsapp = { ...state.whatsapp, ...action.payload };
        },
        setWhatsAppLoading: (state, action) => {
            state.whatsapp.isLoading = action.payload;
        },
        setSourceStatusData: (state, action) => {
            state.sourceStatusData = { ...state.sourceStatusData, ...action.payload };
        },
        setSourceStatusLoading: (state, action) => {
            state.sourceStatusData.isLoading = action.payload;
        },
        setReferralStatusData: (state, action) => {
            state.referralStatusData = { ...state.referralStatusData, ...action.payload };
        },
        setReferralStatusLoading: (state, action) => {
            state.referralStatusData.isLoading = action.payload;
        },
        setLandingUrlData: (state, action) => {
            state.landingUrlData = { ...state.landingUrlData, ...action.payload };
        },
        setLandingUrlLoading: (state, action) => {
            state.landingUrlData.isLoading = action.payload;
        },
        setLandingUrlProgress: (state, action) => {
            state.landingUrlData.progress = action.payload;
        },
        setClinicianConversion: (state, action) => {
            state.clinicianConversion = { ...state.clinicianConversion, ...action.payload };
        },
        setClinicianConversionLoading: (state, action) => {
            state.clinicianConversion.isLoading = action.payload;
        },
        setTcoPerformance: (state, action) => {
            state.tcoPerformance = { ...state.tcoPerformance, ...action.payload };
        },
        setTcoPerformanceLoading: (state, action) => {
            state.tcoPerformance.isLoading = action.payload;
        },
        setLostLeadsData: (state, action) => {
            state.lostLeads = { ...state.lostLeads, ...action.payload, isLoading: false };
        },
        setLostLeadsLoading: (state, action) => {
            state.lostLeads.isLoading = action.payload;
        },
        // Click Tracking reducers
        clickTrackingLoading: (state) => {
            state.clickTracking.isLoading = true;
            state.clickTracking.error = null;
        },
        clickTrackingSuccess: (state, action) => {
            const { events, kpis, _fetchKey, formSubmissionsByPage } = action.payload;
            state.clickTracking.events = events;
            state.clickTracking.kpis = kpis;
            state.clickTracking._fetchKey = _fetchKey;
            state.clickTracking.formSubmissionsByPage = formSubmissionsByPage || [];
            state.clickTracking.isLoading = false;
            state.clickTracking.error = null;
        },
        clickTrackingError: (state, action) => {
            state.clickTracking.isLoading = false;
            state.clickTracking.error = action.payload;
        },
        // Clears the dedup key so the next effect pass re-fires the thunk.
        // Used by the Retry button on the full-error state.
        clearClickTrackingFetchKey: (state) => {
            state.clickTracking._fetchKey = '';
            state.clickTracking.error = null;
        },
    },
});

export const {
    setLoading,
    setError,
    setDateRange,
    setKpis,
    setFunnel,
    setTreatmentBreakdown,
    setTopPractitioners,
    setMonthlyTrend,
    setSelectedTreatment,
    setTreatmentTotalLeads,
    setTreatmentList,
    setTreatmentKpis,
    setTreatmentFunnel,
    setTreatmentMonthly,
    setTreatmentPractitioners,
    setMonthlyEnquiries,
    setMonthlyEnquiriesLoading,
    setTreatmentStatusSummary,
    setTreatmentStatusSummaryLoading,
    setTreatmentFunnelData,
    setPractitioners,
    setPractitionersPage,
    setPractitionersSortBy,
    setPractitionersSearch,
    setPractitionerStats,
    setPractitionerStatsLoading,
    setSources,
    setCampaigns,
    setSourceInsights,
    setSelectedMetric,
    setTrendData,
    setYoyData,
    setSeasonality,
    setForecast,
    setMilestones,
    setArchivedLeads,
    setLeadPopup,
    setOverviewSources,
    setOverviewReferrals,
    setWhatsAppData,
    setWhatsAppLoading,
    setSourceStatusData,
    setSourceStatusLoading,
    setReferralStatusData,
    setReferralStatusLoading,
    setLandingUrlData,
    setLandingUrlLoading,
    setLandingUrlProgress,
    setClinicianConversion,
    setClinicianConversionLoading,
    setTcoPerformance,
    setTcoPerformanceLoading,
    setLostLeadsData,
    setLostLeadsLoading,
    clickTrackingLoading,
    clickTrackingSuccess,
    clickTrackingError,
    clearClickTrackingFetchKey,
    setFetchKey,
    clearFetchKeys,
    setReportPracticeIds,
} = slice.actions;

// Back-compat export — accepts a single id and wraps it.
export const setReportPracticeId = (id) => setReportPracticeIds(id != null ? [id] : null);

// Helper functions - exported for use in components
export const calculateTrend = (current, previous) => ({
    direction: current >= previous ? 'up' : 'down',
    percentage: previous > 0 ? Math.abs(((current - previous) / previous) * 100).toFixed(1) : 0,
});

export const calculateConversion = (from, to) => (from > 0 ? ((to / from) * 100).toFixed(1) : 0);

// Thunks
// Calculate previous period dates for comparison
export function calculatePreviousPeriod(dateRange, comparisonType) {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const durationMs = end.getTime() - start.getTime();
    const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));

    let prevStart;
    let prevEnd;

    if (comparisonType === 'yoy') {
        // Same dates, one year back
        prevStart = new Date(start);
        prevStart.setFullYear(prevStart.getFullYear() - 1);
        prevEnd = new Date(end);
        prevEnd.setFullYear(prevEnd.getFullYear() - 1);
    } else {
        // Previous equivalent period (shift back by duration)
        prevEnd = new Date(start.getTime() - 1000); // 1 second before current start
        prevStart = new Date(prevEnd.getTime() - durationDays * 24 * 60 * 60 * 1000);
    }

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return {
        start: `${fmt(prevStart)} 00:00:00`,
        end: `${fmt(prevEnd)} 23:59:59`,
    };
}

export function fetchOverviewData(practiceIdOrIds, dateRange, comparisonType = 'period') {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    // Multi-practice aggregation: when more than one practice is selected, the
    // backend's `practice__in` aggregates everything in a single round-trip.
    // `pSuffix` appends `&practice__in=1,2,3` or "" when only one practice is
    // selected (the URL's practice_pk already scopes it).
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Period vs YoY comparison determines which API strategy to use
            const useSummaryEndpoint = comparisonType === 'period';

            // Shared calls (same for both period-vs-period and year-over-year paths).
            // Archived data is NOT included here — see fetchArchivedLeadsInPeriod /
            // fetchArchivedCountForPeriod below. The backend's /reports/leads/ endpoint
            // only filters archived leads by created_at, which is semantically wrong
            // for a "Lost in period" KPI.
            const sharedCalls = [
                callApi(`${baseUrl}/practice-user/${ids.length > 1 ? `?practice__in=${ids.join(',')}` : ''}`),
                callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__referral]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_treatments__treatment__treatment_item__treatment_category__name]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ];

            // Overview loads fast. Two archive reads:
            //   - KPI count: /reports/archived-lead-summary/ (archived_at
            //     accurate, ≤100ms)
            //   - byReason breakdown: fast group_by keyed on created_at
            //     (backend caveat). This can legitimately disagree with
            //     the widget on narrow windows; the Lost Leads PAGE uses
            //     archived_at pagination for strict parity.
            // DO NOT paginate /archived/ here — on practices with 1000+
            // archived leads Overview blocks on 20+ sequential calls and
            // hangs on the spinner.
            // archivedDataPromise fetches current + previous count and a
            // per-reason breakdown. When the backend supports
            // `?breakdown=reason` (feature_reporting_phase_2, now
            // universally deployed) the summary endpoint returns
            // `by_reason` and we use that directly — archived_at
            // filter, so widget total and reason sums always agree.
            //
            // The legacy group_by fallback is fetched LAZILY below only
            // if the summary didn't include a breakdown. Previously it
            // ran in parallel on every Overview load and its result was
            // discarded — an extra round-trip per render for a fallback
            // path that's dormant in production. Review phase 5 flagged
            // this as dead-weight perf cost.
            const prevPeriodForArchive = calculatePreviousPeriod(dateRange, comparisonType);
            const archivedDataPromise = Promise.all([
                fetchArchivedSummaryWithBreakdown(ids, dateRange),
                fetchArchivedCountForPeriod(ids, prevPeriodForArchive),
            ]);

            let totalLeads; let consultationsBooked; let attended; let treatmentStarted; let completed; let fta;
            // Funnel uses historical counts (how many ever passed through each stage)
            let funnelBooked; let funnelAttended; let funnelStarted; let funnelCompleted; let funnelFta;
            let prevTotalLeads; let prevConsultationsBooked; let prevAttended; let prevTreatmentStartedCount;
            let practitionerStatsRes; let treatmentDetailsRes;
            let leadSourceRes; let referralRes; let treatmentRevenueRes;

            if (useSummaryEndpoint) {
                // Period comparison: uses lead-status-summary for current KPIs + direct calls for previous period
                // Funnel uses state_transition_params for historical counts (how many ever passed through each stage)
                const prevPeriod = calculatePreviousPeriod(dateRange, comparisonType);
                const prevTreatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationAttendedParams = encodeURIComponent(`{"state":"CONSULTATION_ATTENDED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);

                // Funnel-specific: state_transition_params for current period (historical counts)
                const funnelBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelAttendedParams = encodeURIComponent(`{"state":"CONSULTATION_ATTENDED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelClosedParams = encodeURIComponent(`{"state":"CLOSED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const funnelFtaParams = encodeURIComponent(`{"state":"FAILED_TO_ATTEND_CONSULTATION","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);

                const [
                    totalLeadsRes,
                    prevTotalLeadsRes,
                    prevConsultationBookedRes,
                    prevConsultationAttendedRes,
                    prevTreatmentStartedRes,
                    funnelBookedRes,
                    funnelAttendedRes,
                    funnelStartedRes,
                    funnelClosedRes,
                    funnelFtaRes,
                    practitionerStatsResLocal,
                    treatmentDetailsResLocal,
                    leadSourceResLocal,
                    referralResLocal,
                    treatmentRevenueResLocal,
                ] = await Promise.all([
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${prevPeriod.start}&created_at__lte=${prevPeriod.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationAttendedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevTreatmentStartedParams}${pSuffix}`),
                    // Funnel: historical counts via state_transition_params (exclude duplicates to match popup)
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelAttendedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelStartedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelClosedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${funnelFtaParams}${pSuffix}`),
                    ...sharedCalls,
                ]);

                practitionerStatsRes = practitionerStatsResLocal;
                treatmentDetailsRes = treatmentDetailsResLocal;
                leadSourceRes = leadSourceResLocal;
                referralRes = referralResLocal;
                treatmentRevenueRes = treatmentRevenueResLocal;

                // Use created_at count for Total Leads (accurate count of leads created in period)
                totalLeads = totalLeadsRes?.data?.data?.count_id || 0;
                prevTotalLeads = prevTotalLeadsRes?.data?.data?.count_id || 0;

                // Extract historical counts from state_transition_params responses
                // These count leads that PASSED THROUGH each status in the date range
                // (not just leads currently sitting in that status)
                const extractCount = (res, state) => (res?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === state
                )?.count_id || 0;
                funnelBooked = extractCount(funnelBookedRes, 'CONSULTATION_BOOKED');
                funnelAttended = extractCount(funnelAttendedRes, 'CONSULTATION_ATTENDED');
                funnelStarted = extractCount(funnelStartedRes, 'TREATMENT_STARTED');
                funnelCompleted = extractCount(funnelClosedRes, 'CLOSED');
                funnelFta = extractCount(funnelFtaRes, 'FAILED_TO_ATTEND_CONSULTATION');

                // KPI cards use same historical transition counts as funnel for consistency
                consultationsBooked = funnelBooked;
                attended = funnelAttended;
                treatmentStarted = funnelStarted;
                completed = funnelCompleted;
                fta = funnelFta;

                // Use direct API calls for previous period status KPIs (lead-status-summary previous data unreliable)
                prevConsultationsBooked = (prevConsultationBookedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                prevAttended = (prevConsultationAttendedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_ATTENDED'
                )?.count_id || 0;
                prevTreatmentStartedCount = (prevTreatmentStartedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;
            } else {
                // YoY comparison: legacy multi-call approach
                const treatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const consultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const consultationAttendedParams = encodeURIComponent(`{"state":"CONSULTATION_ATTENDED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const closedParams = encodeURIComponent(`{"state":"CLOSED","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);
                const ftaParams = encodeURIComponent(`{"state":"FAILED_TO_ATTEND_CONSULTATION","timestamp_gte":"${dateRange.start}","timestamp_lte":"${dateRange.end}"}`);

                const prevPeriod = calculatePreviousPeriod(dateRange, comparisonType);
                const prevTreatmentStartedParams = encodeURIComponent(`{"state":"TREATMENT_STARTED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationBookedParams = encodeURIComponent(`{"state":"CONSULTATION_BOOKED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);
                const prevConsultationAttendedParams = encodeURIComponent(`{"state":"CONSULTATION_ATTENDED","timestamp_gte":"${prevPeriod.start}","timestamp_lte":"${prevPeriod.end}"}`);

                const [
                    totalLeadsRes,
                    consultationBookedRes,
                    consultationAttendedRes,
                    treatmentStartedRes,
                    closedRes,
                    ftaRes,
                    practitionerStatsResLocal,
                    treatmentDetailsResLocal,
                    leadSourceResLocal,
                    referralResLocal,
                    treatmentRevenueResLocal,
                    prevTotalLeadsRes,
                    prevConsultationBookedRes,
                    prevConsultationAttendedRes,
                    prevTreatmentStartedRes,
                ] = await Promise.all([
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${consultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${consultationAttendedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${treatmentStartedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${closedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${ftaParams}${pSuffix}`),
                    ...sharedCalls,
                    callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${prevPeriod.start}&created_at__lte=${prevPeriod.end}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationBookedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevConsultationAttendedParams}${pSuffix}`),
                    callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${prevTreatmentStartedParams}${pSuffix}`),
                ]);

                practitionerStatsRes = practitionerStatsResLocal;
                treatmentDetailsRes = treatmentDetailsResLocal;
                leadSourceRes = leadSourceResLocal;
                referralRes = referralResLocal;
                treatmentRevenueRes = treatmentRevenueResLocal;

                totalLeads = totalLeadsRes?.data?.data?.count_id || 0;
                const consultationBookedData = consultationBookedRes?.data?.data || [];
                const consultationAttendedData = consultationAttendedRes?.data?.data || [];
                const treatmentStartedData = treatmentStartedRes?.data?.data || [];

                consultationsBooked = consultationBookedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                attended = consultationAttendedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_ATTENDED'
                )?.count_id || 0;
                treatmentStarted = treatmentStartedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;

                const closedData = closedRes?.data?.data || [];
                const ftaData = ftaRes?.data?.data || [];
                completed = closedData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CLOSED'
                )?.count_id || 0;
                fta = ftaData.find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'FAILED_TO_ATTEND_CONSULTATION'
                )?.count_id || 0;

                // YoY path: KPI values are already from state_transition_params (historical), reuse for funnel
                funnelBooked = consultationsBooked;
                funnelAttended = attended;
                funnelStarted = treatmentStarted;
                funnelCompleted = completed;
                funnelFta = fta;

                prevTotalLeads = prevTotalLeadsRes?.data?.data?.count_id || 0;
                prevConsultationsBooked = (prevConsultationBookedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_BOOKED'
                )?.count_id || 0;
                prevAttended = (prevConsultationAttendedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'CONSULTATION_ATTENDED'
                )?.count_id || 0;
                prevTreatmentStartedCount = (prevTreatmentStartedRes?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === 'TREATMENT_STARTED'
                )?.count_id || 0;
            }

            // Process shared data (same for both paths)
            const practitionerStatsRaw = practitionerStatsRes?.data;
            const practitionerStats = Array.isArray(practitionerStatsRaw)
                ? practitionerStatsRaw
                : (practitionerStatsRaw?.results || []);
            const treatmentDetails = treatmentDetailsRes?.data?.results || [];
            const sourceStats = leadSourceRes?.data?.data || [];
            const referralStats = referralRes?.data?.data || [];
            const treatmentRevenueData = treatmentRevenueRes?.data?.data || [];

            // Destructure: current summary returns {count, by_reason}; previous
            // is still just a count (we don't need the breakdown for the MoM
            // comparison, just the headline number).
            const [currentSummary, prevLostLeads] = await archivedDataPromise;
            const lostLeads = currentSummary?.count ?? 0;
            // Prefer the summary's by_reason (archived_at-filtered, matches
            // the widget total). Fall back to the legacy group_by ONLY when
            // the backend didn't supply by_reason — a defensive path for
            // rolled-back deploys, not the steady-state code path.
            let archivedByReason = currentSummary?.byReason;
            if (!archivedByReason) {
                const archivedByReasonRes = await callApi(
                    `${baseUrl}/leads/?group_by[lead_notes__note]&aggregate[Count]=id&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`,
                );
                const archivedByReasonData = archivedByReasonRes?.data?.data || [];
                archivedByReason = archivedByReasonData
                    .filter((item) => item.lead_notes__note && VALID_ARCHIVE_REASONS.includes(item.lead_notes__note))
                    .map((item) => ({
                        reason: item.lead_notes__note,
                        label: formatArchiveReasonLabel(item.lead_notes__note),
                        count: item.count_id || 0,
                    }))
                    .sort((a, b) => b.count - a.count);
            }

            dispatch(setArchivedLeads({
                total: lostLeads,
                byReason: archivedByReason,
            }));

            // Set KPIs with comparison data
            dispatch(setKpis({
                totalLeads: { value: totalLeads, previousValue: prevTotalLeads },
                consultationsBooked: { value: consultationsBooked, previousValue: prevConsultationsBooked },
                attended: { value: attended, previousValue: prevAttended },
                treatmentStarted: { value: treatmentStarted, previousValue: prevTreatmentStartedCount },
                lostLeads: { value: lostLeads, previousValue: prevLostLeads },
            }));

            // Build funnel data
            // Funnel uses historical counts (state_transition_params) so bar matches popup
            dispatch(setFunnel([
                // Funnel palette — one unique colour per stage, keyed off the lead-status chip palette:
                // blue → purple → amber/yellow → green → grey → orange → red.
                { stage: 'Total Leads', value: totalLeads, color: 'linear-gradient(90deg, #3b82f6, #60a5fa)' },
                { stage: 'Consultations Booked', value: funnelBooked, color: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' },
                { stage: 'Attended', value: funnelAttended, color: 'linear-gradient(90deg, #f59e0b, #fbbf24)' },
                { stage: 'Treatment Started', value: funnelStarted, color: 'linear-gradient(90deg, #10b981, #34d399)' },
                { stage: 'Completed', value: funnelCompleted, color: 'linear-gradient(90deg, #64748b, #94a3b8)' },
                { stage: 'Failed to Attend', value: funnelFta, color: 'linear-gradient(90deg, #f97316, #fb923c)' },
                { stage: 'Archived', value: lostLeads, color: 'linear-gradient(90deg, #ef4444, #f87171)' },
            ]));

            // Build treatment breakdown from treatment details with actual revenue
            const treatmentCategoryMap = {};
            treatmentDetails.forEach((t) => {
                const catId = t.treatment_item?.treatment_category?.id;
                const catName = t.treatment_item?.treatment_category?.name;
                if (catId && catName) {
                    if (!treatmentCategoryMap[catId]) {
                        treatmentCategoryMap[catId] = {
                            id: catId,
                            name: catName,
                            color: t.colour || '#6366f1',
                            count: 0,
                            revenue: 0,
                        };
                    }
                    treatmentCategoryMap[catId].count += 1;
                }
            });

            // Merge in actual revenue and lead counts from treatment revenue data
            treatmentRevenueData.forEach((revenueItem) => {
                const catName = revenueItem.lead_treatments__treatment__treatment_item__treatment_category__name;
                if (catName) {
                    // Find matching category by name
                    const matchingCategory = Object.values(treatmentCategoryMap).find(
                        (c) => c.name?.toLowerCase() === catName?.toLowerCase()
                    );
                    if (matchingCategory) {
                        matchingCategory.count = revenueItem.count_id || matchingCategory.count;
                        matchingCategory.revenue = revenueItem.sum_lead_treatments__custom_price || 0;
                    } else {
                        // Add category if not found in treatment details
                        const newId = `cat_${catName}`;
                        treatmentCategoryMap[newId] = {
                            id: newId,
                            name: catName,
                            color: '#6366f1',
                            count: revenueItem.count_id || 0,
                            revenue: revenueItem.sum_lead_treatments__custom_price || 0,
                        };
                    }
                }
            });
            dispatch(setTreatmentBreakdown(Object.values(treatmentCategoryMap).filter((c) => c.count > 0 || c.revenue > 0)));

            // Process and dispatch source data
            const sources = sourceStats
                .filter((s) => s.lead_source__type) // Filter out null types
                .map((s) => ({
                    type: s.lead_source__type,
                    leads: s.count_id || 0,
                    revenue: s.sum_lead_treatments__custom_price || 0,
                }))
                .sort((a, b) => b.leads - a.leads);
            dispatch(setOverviewSources(sources));

            // Process and dispatch referral data
            const referrals = referralStats
                .filter((r) => r.lead_source__referral) // Filter out null referrals
                .map((r) => ({
                    name: r.lead_source__referral,
                    leads: r.count_id || 0,
                    revenue: r.sum_lead_treatments__custom_price || 0,
                }))
                .sort((a, b) => b.leads - a.leads);
            dispatch(setOverviewReferrals(referrals));

            // Set top practitioners - filter to only include dentists (role === 'Practitioner')
            const dentistsOnly = practitionerStats.filter((p) => p.role === 'Practitioner');
            const sortedPractitioners = [...dentistsOnly]
                .sort((a, b) => (b.conversions_value || 0) - (a.conversions_value || 0))
                .slice(0, 5)
                .map((p, idx) => ({
                    rank: idx + 1,
                    id: p.user?.id,
                    name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim(),
                    avatar: p.user?.avatar,
                    conversions: p.conversions_count || 0,
                    revenue: p.conversions_value || 0,
                    conversionRate: p.consultations_count > 0 ? ((p.conversions_count / p.consultations_count) * 100).toFixed(1) : 0,
                }));
            dispatch(setTopPractitioners(sortedPractitioners));

            dispatch(setFetchKey({ key: '_overviewFetchKey', value: `${practiceId}|${dateRange.start}|${dateRange.end}|${comparisonType}` }));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch overview analytics', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
            dispatch(updateError({ message: error.message || 'Analytics load failed', success: false }));
        }
    };
}

export function fetchPractitionersData(practiceIdOrIds) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;
        // Multi-practice aggregation — PracticeUserConversionReport accepts
        // practice__in and unions practitioners across every selected practice.
        const practiceInQuery = ids.length > 1 ? `?practice__in=${ids.join(',')}` : '';

        try {
            // Note: The practice-user endpoint returns all-time stats
            // Date filtering is not supported for practitioner statistics
            const practitionerStatsRes = await callApi(`${baseUrl}/practice-user/${practiceInQuery}`);

            // Handle practitioner stats - could be array directly or have results property
            const practitionerStatsRaw = practitionerStatsRes?.data;
            const allPractitionerStats = Array.isArray(practitionerStatsRaw)
                ? practitionerStatsRaw
                : (practitionerStatsRaw?.results || []);

            // Filter to only include dentists (role === 'Practitioner')
            const practitionerStats = allPractitionerStats.filter((p) => p.role === 'Practitioner');

            const practitioners = practitionerStats.map((p, idx) => ({
                id: p.user?.id,
                rank: idx + 1,
                name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim(),
                avatar: p.user?.avatar,
                email: p.user?.email,
                conversions: p.conversions_count || 0,
                revenue: p.conversions_value || 0,
                consultations: p.consultations_count || 0,
                conversionRate: p.consultations_count > 0
                    ? ((p.conversions_count / p.consultations_count) * 100).toFixed(1)
                    : 0,
            }));

            // Sort by revenue by default
            practitioners.sort((a, b) => b.revenue - a.revenue);
            practitioners.forEach((p, idx) => { p.rank = idx + 1; });

            dispatch(setPractitioners(practitioners));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch practitioners data', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

export function fetchSourcesData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            const [leadSourceRes, referralRes] = await Promise.all([
                callApi(`${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                callApi(`${baseUrl}/leads/?group_by[lead_source__referral]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ]);

            const sourceStats = leadSourceRes?.data?.data || [];
            const referralStats = referralRes?.data?.data || [];

            const sources = sourceStats.map((s) => ({
                type: s.lead_source__type || 'Unknown',
                leads: s.count_id || 0,
                revenue: s.sum_lead_treatments__custom_price || 0,
                conversionRate: 0, // Would need additional calculation
            }));

            dispatch(setSources(sources));

            // Process referrals as campaigns
            const campaigns = referralStats
                .filter((r) => r.lead_source__referral)
                .map((r) => ({
                    name: r.lead_source__referral,
                    leads: r.count_id || 0,
                    revenue: r.sum_lead_treatments__custom_price || 0,
                }));

            dispatch(setCampaigns(campaigns));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch sources data', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

// Fetch source status breakdown (for By Source page)
export function fetchSourceStatusData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setSourceStatusLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Two queries: one for unique lead counts per source (no treatment grouping),
            // another for status breakdown (grouped by treatment status).
            // The first avoids overcounting leads that have multiple treatments.
            const [sourceEnquiryRes, sourceStatusRes] = await Promise.all([
                // Unique leads per source — no lead_treatments__ in group_by, so one row per lead per source
                callApi(
                    `${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`
                ),
                // Status breakdown per source — grouped by treatment status (may overcount leads, used only for per-status columns)
                callApi(
                    `${baseUrl}/leads/?group_by[lead_source__type]&group_by[lead_treatments__status]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`
                ),
            ]);

            const sourceEnquiryRaw = sourceEnquiryRes?.data?.data || [];
            const sourceStatusRaw = sourceStatusRes?.data?.data || [];

            // Build a map of unique lead counts per source from the ungrouped query
            const enquiryCountMap = {};
            sourceEnquiryRaw.forEach((item) => {
                const sourceType = item.lead_source__type || 'Unknown';
                enquiryCountMap[sourceType] = item.count_id || 0;
            });

            // Transform data: group by source type, aggregate status counts
            const sourceMap = {};
            sourceStatusRaw.forEach((item) => {
                const sourceType = item.lead_source__type || 'Unknown';
                const status = item.lead_treatments__status || 'NEW';
                const count = item.count_id || 0;
                const revenue = item.sum_lead_treatments__custom_price || 0;

                if (!sourceMap[sourceType]) {
                    sourceMap[sourceType] = {
                        id: sourceType,
                        sourceType,
                        enquiries: 0,
                        NEW: 0,
                        CONSULTATION_BOOKED: 0,
                        CONSULTATION_ATTENDED: 0,
                        TREATMENT_STARTED: 0,
                        FAILED_TO_ATTEND_CONSULTATION: 0,
                        CLOSED: 0,
                        revenue: 0,
                    };
                }

                // Revenue == realised revenue from completed treatments only.
                // Summing across all statuses (pipeline value) is misleading
                // and made the Revenue cell click disagree with the popup,
                // which filters to CLOSED.
                if (status === 'CLOSED') {
                    sourceMap[sourceType].revenue += revenue;
                }

                if (Object.prototype.hasOwnProperty.call(sourceMap[sourceType], status)) {
                    sourceMap[sourceType][status] += count;
                }
            });

            // Set enquiries from the unique-lead query (not the treatment-grouped one)
            Object.keys(sourceMap).forEach((sourceType) => {
                sourceMap[sourceType].enquiries = enquiryCountMap[sourceType] || 0;
            });

            // Calculate conversion rate for each source
            const sourceData = Object.values(sourceMap).map((source) => ({
                ...source,
                conversionRate: source.enquiries > 0
                    ? ((source.TREATMENT_STARTED / source.enquiries) * 100).toFixed(1)
                    : 0,
            }));

            // Sort by enquiries descending
            sourceData.sort((a, b) => b.enquiries - a.enquiries);

            dispatch(setFetchKey({ key: '_sourceFetchKey', value: `${ids.join(',')}|${dateRange.start}|${dateRange.end}` }));
            dispatch(setSourceStatusData({
                data: sourceData,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch source status data', error);
            dispatch(setSourceStatusData({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Fetch referral status breakdown (for By Source page)
export function fetchReferralStatusData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setReferralStatusLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Two queries: one for unique lead counts per referral (no treatment grouping),
            // another for status breakdown (grouped by treatment status).
            // The first avoids overcounting leads that have multiple treatments.
            const [referralEnquiryRes, referralStatusRes] = await Promise.all([
                // Unique leads per referral — no lead_treatments__ in group_by, so one row per lead per referral
                callApi(
                    `${baseUrl}/leads/?group_by[lead_source__referral]&aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`
                ),
                // Status breakdown per referral — grouped by treatment status (may overcount leads, used only for per-status columns)
                callApi(
                    `${baseUrl}/leads/?group_by[lead_source__referral]&group_by[lead_treatments__status]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`
                ),
            ]);

            const referralEnquiryRaw = referralEnquiryRes?.data?.data || [];
            const referralStatusRaw = referralStatusRes?.data?.data || [];

            // Build a map of unique lead counts per referral from the ungrouped query
            const enquiryCountMap = {};
            referralEnquiryRaw.forEach((item) => {
                const referral = item.lead_source__referral;
                if (!referral) return;
                enquiryCountMap[referral] = item.count_id || 0;
            });

            // Transform data: group by referral, aggregate status counts
            const referralMap = {};
            referralStatusRaw.forEach((item) => {
                const referral = item.lead_source__referral;
                // Skip null/empty referrals
                if (!referral) return;

                const status = item.lead_treatments__status || 'NEW';
                const count = item.count_id || 0;
                const revenue = item.sum_lead_treatments__custom_price || 0;

                if (!referralMap[referral]) {
                    referralMap[referral] = {
                        id: referral,
                        referral,
                        enquiries: 0,
                        NEW: 0,
                        CONSULTATION_BOOKED: 0,
                        CONSULTATION_ATTENDED: 0,
                        TREATMENT_STARTED: 0,
                        FAILED_TO_ATTEND_CONSULTATION: 0,
                        CLOSED: 0,
                        revenue: 0,
                    };
                }

                // Realised revenue only — see same guard in fetchSourceStatusData
                if (status === 'CLOSED') {
                    referralMap[referral].revenue += revenue;
                }

                if (Object.prototype.hasOwnProperty.call(referralMap[referral], status)) {
                    referralMap[referral][status] += count;
                }
            });

            // Set enquiries from the unique-lead query (not the treatment-grouped one)
            Object.keys(referralMap).forEach((referral) => {
                referralMap[referral].enquiries = enquiryCountMap[referral] || 0;
            });

            // Calculate conversion rate for each referral
            const referralData = Object.values(referralMap).map((referral) => ({
                ...referral,
                conversionRate: referral.enquiries > 0
                    ? ((referral.TREATMENT_STARTED / referral.enquiries) * 100).toFixed(1)
                    : 0,
            }));

            // Sort by enquiries descending
            referralData.sort((a, b) => b.enquiries - a.enquiries);

            dispatch(setFetchKey({ key: '_referralFetchKey', value: `${ids.join(',')}|${dateRange.start}|${dateRange.end}` }));
            dispatch(setReferralStatusData({
                data: referralData,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch referral status data', error);
            dispatch(setReferralStatusData({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Fetch landing-page breakdown — buckets leads by their `entire_url` metadata
// (the form-submission landing URL captured at submit time, same field
// rendered on the lead-detail Meta source view). `entire_url` can live in
// either `lead.lead_metadata` (array of {key,value}) or `lead.lead_source.metadata`
// (JSON object) depending on how the form integration wrote it — check both.
//
// Aggregate group_by on a metadata sub-key isn't reliable, so we paginate
// /leads/ for the period and bucket client-side. 6-wide concurrency mirrors
// TreatmentLeadPopup.
//
// Leads with no entire_url (organic / direct / embeds that didn't capture it)
// fall under a synthetic "(no landing URL)" row so coverage is visible.
export function fetchLandingUrlData(practiceIdOrIds, dateRange) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    const NO_URL = '(no landing URL)';
    // Capture cache key up-front so we can (a) reserve the slot before any
    // network call, blocking duplicate concurrent dispatches, and (b) verify
    // the request is still current at each await boundary — otherwise a slow
    // 5000-lead paginated run could clobber the data for a more recent
    // practice/date selection.
    const cacheKey = `${ids.join(',')}|${dateRange.start}|${dateRange.end}`;
    return async (dispatch, getState) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        const isStale = () => getState().analyticsReports._landingUrlFetchKey !== cacheKey;
        // Reserve the key BEFORE any network call so identical in-flight
        // dispatches see _landingUrlFetchKey === cacheKey and stop early.
        // The CrossPracticeTable / CompareTab dispatchers already do a
        // pre-check; this guard keeps us safe even if a caller forgets.
        dispatch(setFetchKey({ key: '_landingUrlFetchKey', value: cacheKey }));
        dispatch(setLandingUrlLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}`;
        const dateFilter = `&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`;
        // Trim payload by limiting fields. We need entire_url which lives in
        // EITHER lead_metadata[].value (key='entire_url') OR lead_source.metadata.entire_url.
        // Note: we DON'T use a `lead_source{metadata}` sub-selector — that
        // syntax isn't reliably honoured by the DRF fields parser on every
        // version. Request the flat `lead_source` field and rely on the
        // serializer to include `metadata` by default.
        const fields = 'fields={id,lead_metadata,lead_source,lead_treatments{status,custom_price}}';

        try {
            // First page tells us the total count
            const firstUrl = `${baseUrl}/leads/?is_duplicate=false${dateFilter}${pSuffix}&${fields}&limit=50`;
            const firstResp = await callApi(firstUrl);
            if (isStale()) return; // a newer fetch took over while we waited
            const firstRaw = firstResp?.data;
            const firstPage = Array.isArray(firstRaw) ? firstRaw : (firstRaw?.results || []);
            const totalCount = firstRaw?.count ?? firstPage.length;
            const pageSize = firstPage.length || 50;
            const cap = Math.min(totalCount, 5000);
            let allLeads = [...firstPage];
            // Surface progress so the table can render "Loading N of M leads..."
            // instead of a featureless spinner for the 30-60s worst case.
            dispatch(setLandingUrlProgress({ loaded: allLeads.length, total: cap }));

            if (allLeads.length < cap) {
                const remaining = [];
                for (let off = pageSize; off < cap; off += pageSize) remaining.push(off);
                const CONCURRENCY = 6;
                for (let i = 0; i < remaining.length; i += CONCURRENCY) {
                    if (isStale()) return; // bail mid-pagination
                    const batch = remaining.slice(i, i + CONCURRENCY);
                    // eslint-disable-next-line no-await-in-loop
                    const pages = await Promise.all(batch.map((off) =>
                        callApi(`${baseUrl}/leads/?is_duplicate=false${dateFilter}${pSuffix}&${fields}&limit=${pageSize}&offset=${off}`)
                    ));
                    if (isStale()) return;
                    const newRows = pages.flatMap((p) => {
                        const r = p?.data;
                        return Array.isArray(r) ? r : (r?.results || []);
                    });
                    allLeads = allLeads.concat(newRows);
                    dispatch(setLandingUrlProgress({ loaded: allLeads.length, total: cap }));
                    if (allLeads.length >= cap) break;
                }
            }

            // Bucket by normalised entire_url. Each lead contributes ONCE to enquiries
            // (unique lead count) and per-treatment to status columns / revenue.
            const urlMap = {};
            const ensureBucket = (url) => {
                if (!urlMap[url]) {
                    urlMap[url] = {
                        id: url,
                        landingUrl: url,
                        enquiries: 0,
                        NEW: 0,
                        CONSULTATION_BOOKED: 0,
                        CONSULTATION_ATTENDED: 0,
                        TREATMENT_STARTED: 0,
                        FAILED_TO_ATTEND_CONSULTATION: 0,
                        CLOSED: 0,
                        revenue: 0,
                    };
                }
                return urlMap[url];
            };

            // Resolve entire_url from whichever location the form integration
            // wrote it to. lead_metadata (array) wins over lead_source.metadata
            // because some practices write to it explicitly per submission.
            const resolveEntireUrl = (lead) => {
                const fromArray = (Array.isArray(lead?.lead_metadata) ? lead.lead_metadata : [])
                    .find((m) => m?.key === 'entire_url')?.value;
                if (fromArray) return String(fromArray).trim();
                const fromSource = lead?.lead_source?.metadata?.entire_url;
                return fromSource ? String(fromSource).trim() : '';
            };

            allLeads.forEach((lead) => {
                // Raw URL → normalised key (host+path + channel suffix). Two
                // URLs that differ only in tracking params collapse into one row.
                const raw = resolveEntireUrl(lead);
                const url = raw ? (normalizeLandingUrl(raw) || NO_URL) : NO_URL;
                const bucket = ensureBucket(url);
                bucket.enquiries += 1;
                const treatments = Array.isArray(lead?.lead_treatments) ? lead.lead_treatments : [];
                if (treatments.length === 0) {
                    // Lead with no attached treatment yet — still goes in NEW so the
                    // 'Not Booked' column doesn't lag behind Enquiries.
                    bucket.NEW += 1;
                    return;
                }
                treatments.forEach((lt) => {
                    const status = lt?.status || 'NEW';
                    if (Object.prototype.hasOwnProperty.call(bucket, status)) {
                        bucket[status] += 1;
                    }
                    // Realised revenue only — keeps the column consistent with
                    // the popup, which filters to CLOSED on the Revenue click.
                    if (status === 'CLOSED') {
                        bucket.revenue += parseFloat(lt?.custom_price || 0) || 0;
                    }
                });
            });

            const landingUrlRows = Object.values(urlMap).map((row) => ({
                ...row,
                conversionRate: row.enquiries > 0
                    ? ((row.TREATMENT_STARTED / row.enquiries) * 100).toFixed(1)
                    : 0,
            }));
            landingUrlRows.sort((a, b) => b.enquiries - a.enquiries);

            if (isStale()) return;
            dispatch(setLandingUrlData({
                data: landingUrlRows,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch landing URL data', error);
            // Don't write to the slot if we're stale — a newer dispatch is in
            // flight and owns the state now.
            if (isStale()) return;
            dispatch(setLandingUrlData({
                data: [],
                isLoading: false,
            }));
            dispatch(updateError({
                success: false,
                message: error?.message || 'Failed to fetch landing URL data',
            }));
            // Release the fetchKey so a subsequent retry isn't deduped away.
            dispatch(setFetchKey({ key: '_landingUrlFetchKey', value: null }));
        }
    };
}

export function fetchTreatmentData(practiceIdOrIds, dateRange, treatmentId = null) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            const [treatmentDetailsRes, treatmentRevenueRes, totalLeadsRes] = await Promise.all([
                // /treatments/ is not a report endpoint — scopes by URL practice_pk.
                // For multi-practice, fan out and concat. Dedup by treatment_item name
                // downstream so two practices offering "Invisalign" show one row.
                ids.length > 1
                    ? Promise.all(ids.map((pid) => callApi(`/api/v1/practices/${pid}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`)))
                    : callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`),
                callApi(`${baseUrl}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[lead_treatments__treatment]&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
                // Ungrouped total: counts unique leads (no double-counting multi-treatment leads, includes archived)
                callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            ]);

            // Multi-practice /treatments/ returns an array of responses; merge and
            // dedupe by treatment_item id (same offering, different practice rows).
            const treatmentDetails = Array.isArray(treatmentDetailsRes)
                ? (() => {
                    const seen = new Map();
                    treatmentDetailsRes.forEach((res) => {
                        (res?.data?.results || []).forEach((t) => {
                            const key = t.treatment_item?.id || t.id;
                            if (!seen.has(key)) seen.set(key, t);
                        });
                    });
                    return Array.from(seen.values());
                })()
                : treatmentDetailsRes?.data?.results || [];
            const treatmentRevenue = treatmentRevenueRes?.data?.data || [];

            // Build treatment list with merged data
            const treatmentList = treatmentDetails.map((t) => {
                const revenueData = treatmentRevenue.find((r) => r.lead_treatments__treatment === t.id);
                return {
                    id: t.id,
                    name: t.treatment_item?.name || 'Unknown',
                    category: t.treatment_item?.treatment_category?.name || 'Other',
                    categoryId: t.treatment_item?.treatment_category?.id,
                    color: t.colour || '#6366f1',
                    price: t.price || 0,
                    leads: revenueData?.count_id || 0,
                    revenue: revenueData?.sum_lead_treatments__custom_price || 0,
                };
            });

            dispatch(setTreatmentList(treatmentList));
            dispatch(setTreatmentTotalLeads(totalLeadsRes?.data?.data?.count_id || 0));

            // If a specific treatment is selected, set its KPIs
            if (treatmentId) {
                const selectedTreatment = treatmentList.find((t) => t.id === treatmentId);
                if (selectedTreatment) {
                    dispatch(setTreatmentKpis({
                        leads: selectedTreatment.leads,
                        revenue: selectedTreatment.revenue,
                        avgValue: selectedTreatment.leads > 0
                            ? (selectedTreatment.revenue / selectedTreatment.leads).toFixed(0)
                            : 0,
                    }));
                }
            }

            dispatch(setFetchKey({ key: '_treatmentFetchKey', value: `${ids.join(',')}|${dateRange.start}|${dateRange.end}|${treatmentId || ''}` }));
            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch treatment data', error);
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

export function fetchTrendsData(practiceIdOrIds, dateRange) {
    // Wires Trends to the real /reports/trends/ endpoint. Response shape:
    //   { trend: [{month,label,leads,consultations,treatments},...],
    //     yoy: {leads,consultations,treatments,revenue → {current,previous,change}},
    //     range: {start,end} }
    // Seasonality / forecast / milestones remain placeholder until their
    // respective backend endpoints ship — the UI keeps a "demo data"
    // banner for those three sections.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pInQuery = ids.length > 1 ? `&practice__in=${ids.join(',')}` : '';
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setLoading(true));

        // Default to last 12 months when no range is supplied.
        const end = dateRange?.end ? String(dateRange.end).split(' ')[0] : new Date().toISOString().slice(0, 10);
        let start = dateRange?.start ? String(dateRange.start).split(' ')[0] : null;
        if (!start) {
            const d = new Date(end);
            d.setFullYear(d.getFullYear() - 1);
            start = d.toISOString().slice(0, 10);
        }

        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/reports/trends/?start=${start}&end=${end}${pInQuery}`
            );
            // 404 or 5xx → fall back to empty state + log so we don't ship
            // placeholder numbers that look real.
            const data = res?.data;
            if (!data || typeof data !== 'object') {
                throw new Error(`Unexpected trends response: ${res?.status || 'no status'}`);
            }

            dispatch(setTrendData(data.trend || []));

            // Map the yoy object into the array shape the existing
            // YearOverYearChart component consumes.
            const yoy = data.yoy || {};
            const yoyRows = [
                ['Leads', yoy.leads],
                ['Consultations', yoy.consultations],
                ['Treatments', yoy.treatments],
                ['Revenue', yoy.revenue],
            ]
                .filter(([, v]) => v && typeof v === 'object')
                .map(([metric, v]) => ({
                    metric,
                    current: v.current ?? 0,
                    previous: v.previous ?? 0,
                    change: v.change ?? null,
                }));
            dispatch(setYoyData(yoyRows));

            // Seasonality / forecast / milestones are optional backend
            // fields that may or may not be populated depending on
            // deploy state. When present, wire them through; otherwise
            // clear to avoid stale placeholder data. Gating the dispatch
            // on field presence means the day the backend starts
            // returning these fields, they flow through automatically
            // without a frontend change. Without the gate, the prior
            // blanket `setSeasonality([])` would clobber any real
            // backend-provided data.
            dispatch(setSeasonality(Array.isArray(data.seasonality) ? data.seasonality : []));
            dispatch(setForecast(Array.isArray(data.forecast) ? data.forecast : []));
            dispatch(setMilestones(Array.isArray(data.milestones) ? data.milestones : []));

            dispatch(setLoading(false));
        } catch (error) {
            console.error('Failed to fetch trends data', error);
            // Empty the four series rather than showing numbers that
            // look real but aren't. UI component should render its empty
            // state.
            dispatch(setTrendData([]));
            dispatch(setYoyData([]));
            dispatch(setSeasonality([]));
            dispatch(setForecast([]));
            dispatch(setMilestones([]));
            dispatch(setLoading(false));
            dispatch(setError(error.message));
        }
    };
}

// Fetch lead IDs for a specific archive reason
export function fetchArchivedLeadIds(practiceId, reason) {
    return async (dispatch) => {
        try {
            const encodedReason = encodeURIComponent(reason);
            let leadIds = [];

            // Use the /archived/ endpoint with archived_reason__in filter
            // This is the same endpoint used by ArchiveLeadTable
            const archivedResponse = await callApi(
                `/api/v1/practices/${practiceId}/archived/?is_archived=true&archived_reason__in=${encodedReason}&limit=1000`
            );
            leadIds = (archivedResponse?.data?.results || []).map((l) => l.id);

            // Format the title nicely
            const formattedReason = formatArchiveReasonLabel(reason);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `Archived Leads: ${formattedReason}`,
            }));
        } catch (error) {
            console.error('Failed to fetch archived lead IDs', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch WhatsApp data
export function fetchWhatsAppData(practiceId, businessId, phoneId, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setWhatsAppLoading(true));

        try {
            // If no WhatsApp credentials, return empty state
            if (!businessId || !phoneId) {
                dispatch(setWhatsAppData({
                    totalConversations: 0,
                    activeConversations: 0,
                    closedConversations: 0,
                    avgResponseTime: 0,
                    within24hr: 0,
                    closeReasons: [],
                    responseTimeDistribution: [
                        { bucket: 'lt1h', label: '< 1 hour', count: 0 },
                        { bucket: '1to4h', label: '1-4 hours', count: 0 },
                        { bucket: '4to24h', label: '4-24 hours', count: 0 },
                        { bucket: 'gt24h', label: '> 24 hours', count: 0 },
                    ],
                    leadConversion: {
                        totalWhatsAppLeads: 0,
                        consultationsBooked: 0,
                        treatmentStarted: 0,
                    },
                }));
                dispatch(setWhatsAppLoading(false));
                return;
            }

            const baseUrl = `/api/v1/practices/${practiceId}`;
            const waBaseUrl = `${baseUrl}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}`;

            // Fetch threads and leads data in parallel
            const [threadsRes, whatsAppLeadsRes] = await Promise.all([
                callApi(`${waBaseUrl}/whatsapp-message-threads/?is_archived_included=true&limit=1000`),
                callApi(`${baseUrl}/reports/leads/?lead_source__type__in=whatsapp,WHATSAPP,Whatsapp&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&aggregate[Count]=id`),
            ]);

            const threads = threadsRes?.data?.results || [];
            const whatsAppLeadsCount = whatsAppLeadsRes?.data?.data?.count_id || 0;

            // Filter threads by date range
            const filteredThreads = threads.filter((t) => {
                const createdTime = new Date(t.created_time);
                const startDate = new Date(dateRange.start);
                const endDate = new Date(dateRange.end);
                return createdTime >= startDate && createdTime <= endDate;
            });

            // Calculate metrics
            const totalConversations = filteredThreads.length;
            const closedThreads = filteredThreads.filter((t) => t.is_closed);
            const closedConversations = closedThreads.length;
            const activeConversations = totalConversations - closedConversations;

            // Group by close reasons
            const closeReasonMap = {};
            closedThreads.forEach((t) => {
                const reason = t.metadata?.close_reason || 'Other';
                if (!closeReasonMap[reason]) {
                    closeReasonMap[reason] = { reason, label: reason, count: 0, threadIds: [] };
                }
                closeReasonMap[reason].count += 1;
                closeReasonMap[reason].threadIds.push(t.id);
            });
            const closeReasons = Object.values(closeReasonMap).sort((a, b) => b.count - a.count);

            // Calculate response times (from messages if available)
            // For now, use placeholder metrics - actual implementation would require message-level data
            const avgResponseTime = 0; // Would calculate from first response times
            const within24hr = totalConversations > 0 ? 85 : 0; // Placeholder percentage

            const responseTimeDistribution = [
                { bucket: 'lt1h', label: '< 1 hour', count: Math.floor(totalConversations * 0.4) },
                { bucket: '1to4h', label: '1-4 hours', count: Math.floor(totalConversations * 0.3) },
                { bucket: '4to24h', label: '4-24 hours', count: Math.floor(totalConversations * 0.15) },
                { bucket: 'gt24h', label: '> 24 hours', count: Math.floor(totalConversations * 0.15) },
            ];

            dispatch(setWhatsAppData({
                totalConversations,
                activeConversations,
                closedConversations,
                avgResponseTime,
                within24hr,
                closeReasons,
                responseTimeDistribution,
                leadConversion: {
                    totalWhatsAppLeads: whatsAppLeadsCount,
                    consultationsBooked: Math.floor(whatsAppLeadsCount * 0.6), // Placeholder
                    treatmentStarted: Math.floor(whatsAppLeadsCount * 0.3), // Placeholder
                },
            }));

            dispatch(setWhatsAppLoading(false));
        } catch (error) {
            console.error('Failed to fetch WhatsApp data', error);
            dispatch(setWhatsAppLoading(false));
            dispatch(updateError({ message: 'Failed to load WhatsApp data', success: false }));
        }
    };
}

// Helper to close lead popup
export function closeLeadPopup() {
    return (dispatch) => {
        dispatch(setLeadPopup({
            open: false,
            practiceId: null,
            leadIds: [],
            title: '',
        }));
    };
}

// Fetch lead IDs by KPI type (Total Leads, Consultations Booked, etc.)
export function fetchLeadIdsByKpiType(practiceId, kpiType, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            let leadIds = [];
            let title = '';

            switch (kpiType) {
                case 'totalLeads': {
                    // All leads in date range
                    const response = await callApi(
                        `${baseUrl}/leads/?created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Total Leads';
                    break;
                }
                case 'consultationsBooked': {
                    // Leads with BOOKED or ATTENDED appointments
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status__in=BOOKED,ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Consultations Booked';
                    break;
                }
                case 'attended': {
                    // Leads with ATTENDED appointments
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status=ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Attended Consultations';
                    break;
                }
                case 'treatmentStarted': {
                    // Leads with TREATMENT_STARTED status
                    const response = await callApi(
                        `${baseUrl}/leads/?lead_treatments__status=TREATMENT_STARTED&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    title = 'Treatment Started';
                    break;
                }
                case 'lostLeads': {
                    // /archived/ silently ignores date filters — paginate + client-side
                    // filter by archived_at (see fetchArchivedLeadsInPeriod helper).
                    const archived = await fetchArchivedLeadsInPeriod(practiceId, dateRange);
                    leadIds = archived.map((l) => l.id);
                    title = 'Lost Leads';
                    break;
                }
                default:
                    break;
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by KPI type', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by funnel stage
export function fetchLeadIdsByFunnelStage(practiceId, stage, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            let leadIds = [];
            const title = stage;

            switch (stage) {
                case 'Total Leads': {
                    const response = await callApi(
                        `${baseUrl}/leads/?created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Consultations Booked': {
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status__in=BOOKED,ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Attended': {
                    const response = await callApi(
                        `${baseUrl}/leads/?appointments__status=ATTENDED&appointments__datetime__gte=${dateRange.start}&appointments__datetime__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                case 'Treatment Started': {
                    const response = await callApi(
                        `${baseUrl}/leads/?lead_treatments__status=TREATMENT_STARTED&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
                    );
                    leadIds = (response?.data?.results || []).map((l) => l.id);
                    break;
                }
                default:
                    break;
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by funnel stage', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by treatment category
export function fetchLeadIdsByTreatment(practiceId, treatmentCategoryName, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedCategoryName = encodeURIComponent(treatmentCategoryName);

            // Try the treatment category filter first
            let response = await callApi(
                `${baseUrl}/leads/?lead_treatments__treatment__treatment_item__treatment_category__name=${encodedCategoryName}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000`
            );
            let leadIds = (response?.data?.results || []).map((l) => l.id);

            // If no results, try filtering by treatment name directly (some treatments might not have categories)
            if (leadIds.length === 0) {
                response = await callApi(
                    `${baseUrl}/leads/?lead_treatments__treatment__treatment_item__name__icontains=${encodedCategoryName}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000`
                );
                leadIds = (response?.data?.results || []).map((l) => l.id);
            }

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${treatmentCategoryName} Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by treatment', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by source type
export function fetchLeadIdsBySource(practiceId, sourceType, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedSourceType = encodeURIComponent(sourceType);

            const response = await callApi(
                `${baseUrl}/leads/?lead_source__type=${encodedSourceType}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            // Format source type label
            const formattedSource = sourceType
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase());

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${formattedSource} Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by source', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by referral
export function fetchLeadIdsByReferral(practiceId, referralName, dateRange) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;
            const encodedReferral = encodeURIComponent(referralName);

            const response = await callApi(
                `${baseUrl}/leads/?lead_source__referral=${encodedReferral}&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${referralName} Referral Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by referral', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Fetch lead IDs by practitioner
export function fetchLeadIdsByPractitioner(practiceId, practitionerId, practitionerName) {
    return async (dispatch) => {
        try {
            const baseUrl = `/api/v1/practices/${practiceId}`;

            const response = await callApi(
                `${baseUrl}/leads/?assigned_to=${practitionerId}&limit=1000&fields={id}`
            );
            const leadIds = (response?.data?.results || []).map((l) => l.id);

            dispatch(setLeadPopup({
                open: true,
                practiceId,
                leadIds,
                title: `${practitionerName}'s Leads`,
            }));
        } catch (error) {
            console.error('Failed to fetch lead IDs by practitioner', error);
            dispatch(updateError({ message: 'Failed to fetch lead details', success: false }));
        }
    };
}

// Helper to transform API response to monthly format
const transformTreatmentStatsToMonthly = (apiData) => {
    const result = [];
    // API returns: { "2024": { "1": { NEW: {...}, CONSULTATION_BOOKED: {...}, ... }, "2": {...}, ... }, "2025": {...} }
    if (apiData && typeof apiData === 'object') {
        Object.entries(apiData).forEach(([year, months]) => {
            if (months && typeof months === 'object') {
                Object.entries(months).forEach(([month, data]) => {
                    result.push({
                        year: parseInt(year, 10),
                        month: parseInt(month, 10),
                        data: data || {},
                    });
                });
            }
        });
    }
    // Sort by year and month descending (most recent first)
    result.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
    });
    return result;
};

// Fetch monthly enquiries data for a specific treatment
export function fetchMonthlyEnquiries(practiceId, treatmentId, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setMonthlyEnquiriesLoading(true));

        try {
            // Build API URL
            let url = `/api/v1/practices/${practiceId}/reports/lead-treatment-statistics/`;
            const params = [];

            if (treatmentId) {
                params.push(`treatment=${treatmentId}`);
            }
            if (dateRange?.start) {
                // Extract just the date part (YYYY-MM-DD) without time
                const startDate = dateRange.start.split(' ')[0];
                params.push(`time_after=${startDate}`);
            }
            if (dateRange?.end) {
                // Add one day so the end date is inclusive — the API treats time_before as exclusive
                const endDate = dayjs(dateRange.end.split(' ')[0]).add(1, 'day').format('YYYY-MM-DD');
                params.push(`time_before=${endDate}`);
            }
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await callApi(url);

            if (response?.status === 200) {
                const monthlyData = transformTreatmentStatsToMonthly(response.data);
                dispatch(setMonthlyEnquiries({
                    data: monthlyData,
                    selectedTreatmentId: treatmentId,
                    isLoading: false,
                }));
            } else {
                dispatch(setMonthlyEnquiries({
                    data: [],
                    isLoading: false,
                }));
            }
        } catch (error) {
            dispatch(setMonthlyEnquiries({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Fetch treatment funnel data using state_transition_params (consistent with Overview funnel).
// treatmentIds: array of practice treatment IDs to filter by, or null for all treatments.
export function fetchTreatmentFunnelData(practiceIdOrIds, dateRange, treatmentIds = null) {
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const pSuffix = practiceInSuffix(ids);
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        // Clear stale data immediately so category switches don't flash old numbers
        dispatch(setTreatmentFunnelData(null));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;
        const treatmentFilter = treatmentIds?.length
            ? `&lead_treatments__treatment__in=${treatmentIds.join(',')}`
            : '';

        const statuses = [
            'CONSULTATION_BOOKED',
            'CONSULTATION_ATTENDED',
            'TREATMENT_STARTED',
            'CLOSED',
            'FAILED_TO_ATTEND_CONSULTATION',
        ];

        try {
            const funnelCalls = statuses.map((status) => {
                const params = encodeURIComponent(JSON.stringify({
                    state: status,
                    timestamp_gte: dateRange.start,
                    timestamp_lte: dateRange.end,
                }));
                return callApi(`${baseUrl}/leads/?group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id&state_transition_params=${params}${treatmentFilter}${pSuffix}`);
            });

            const totalLeadsCall = callApi(
                `${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${treatmentFilter}${pSuffix}`
            );
            // Archived count via the original fast group_by query. Yes, this is
            // "created_at in period AND archived" rather than "archived_at in period"
            // — but it's fast, consistent with the rest of the funnel (which also
            // uses created_at/state_transition semantics), and the By Treatment
            // funnel was working fine this way before.
            const archivedCall = callApi(
                `${baseUrl}/leads/?group_by[is_archived]&aggregate[Count]=id&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${treatmentFilter}${pSuffix}`
            );

            const [bookedRes, attendedRes, startedRes, closedRes, ftaRes, totalRes, archivedRes] =
                await Promise.all([...funnelCalls, totalLeadsCall, archivedCall]);
            const archivedCount = (archivedRes?.data?.data || []).find((x) => x.is_archived)?.count_id || 0;

            const extractCount = (res, state) =>
                (res?.data?.data || []).find(
                    (x) => x.lead_treatments__state_transition_logs__state === state
                )?.count_id || 0;

            dispatch(setTreatmentFunnelData({
                totalLeads: totalRes?.data?.data?.count_id || 0,
                consultationsBooked: extractCount(bookedRes, 'CONSULTATION_BOOKED'),
                consultationsAttended: extractCount(attendedRes, 'CONSULTATION_ATTENDED'),
                treatmentStarted: extractCount(startedRes, 'TREATMENT_STARTED'),
                completed: extractCount(closedRes, 'CLOSED'),
                fta: extractCount(ftaRes, 'FAILED_TO_ATTEND_CONSULTATION'),
                archived: archivedCount || 0,
            }));
        } catch (error) {
            dispatch(setTreatmentFunnelData(null));
        }
    };
}

// Fetch treatment status summary for all treatments
export function fetchTreatmentStatusSummary(practiceIdOrIds, treatments, dateRange) {
    // NOTE: /reports/lead-treatment-statistics/ is single-practice only — its
    // PracticeTarget semantics don't translate cleanly to multi-practice
    // aggregation (each practice has its own targets per treatment item).
    // Multi-practice callers get the primary practice's numbers here. Full
    // multi-practice support for this specific endpoint is future work.
    //
    // Activity mode: uses the same /reports/lead-treatment-statistics/ endpoint as
    // MonthlyEnquiriesTable — the authoritative backend aggregation endpoint.
    // Groups by TreatmentItem ID (not PracticeTreatment ID) to avoid split/dropped rows.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setTreatmentStatusSummaryLoading(true));

        try {
            const treatmentPromises = treatments.map(async (treatment) => {
                const treatmentItemId = treatment.treatment_item?.id;
                if (!treatmentItemId) return null;

                let url = `/api/v1/practices/${practiceId}/reports/lead-treatment-statistics/`;
                const params = [`treatment=${treatmentItemId}`];

                if (dateRange?.start) {
                    const startDate = dateRange.start.split(' ')[0];
                    params.push(`time_after=${startDate}`);
                }
                if (dateRange?.end) {
                    // Add one day so the end date is inclusive — the API treats time_before as exclusive
                    const endDate = dayjs(dateRange.end.split(' ')[0]).add(1, 'day').format('YYYY-MM-DD');
                    params.push(`time_before=${endDate}`);
                }

                url += `?${params.join('&')}`;

                try {
                    const response = await callApi(url);
                    if (response?.status === 200 && response.data) {
                        const totals = {
                            NEW: 0,
                            CONSULTATION_BOOKED: 0,
                            CONSULTATION_ATTENDED: 0,
                            TREATMENT_STARTED: 0,
                            CLOSED: 0,
                        };

                        Object.values(response.data).forEach((yearData) => {
                            if (yearData && typeof yearData === 'object') {
                                Object.values(yearData).forEach((monthData) => {
                                    if (monthData && typeof monthData === 'object') {
                                        Object.entries(monthData).forEach(([status, statusData]) => {
                                            if (Object.prototype.hasOwnProperty.call(totals, status) && statusData?.actual) {
                                                totals[status] += statusData.actual;
                                            }
                                        });
                                    }
                                });
                            }
                        });

                        const total = totals.NEW;

                        return {
                            id: treatment.id,
                            treatmentId: treatment.id,
                            treatmentItemId,
                            treatmentName: treatment.treatment_item?.name || 'Unknown',
                            color: treatment.colour || '#6366f1',
                            ...totals,
                            total,
                        };
                    }
                } catch (err) {
                    // Silently fail for individual treatments
                }
                return null;
            });

            const results = await Promise.all(treatmentPromises);
            // Activity mode counts leads moving INTO each status during the period,
            // so a treatment with 0 new enquiries can still legitimately have non-
            // zero bookings/attendances from leads that enquired earlier — keep
            // those rows. Filtering on r.total (= NEW only) was hiding them.
            const validResults = results.filter((r) => {
                if (!r) return false;
                return (r.NEW || 0) + (r.CONSULTATION_BOOKED || 0) + (r.CONSULTATION_ATTENDED || 0)
                    + (r.TREATMENT_STARTED || 0) + (r.CLOSED || 0) > 0;
            });

            dispatch(setTreatmentStatusSummary({
                data: validResults,
                isLoading: false,
            }));
        } catch (error) {
            dispatch(setTreatmentStatusSummary({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Cohort mode: runs a separate API call per status using state_transition_params +
// created_at filtering — same approach as CategorySummaryTable's "Current month" toggle,
// but scoped to the user's selected date range instead of hardcoded current month.
export function fetchTreatmentStatusSummaryCohort(practiceId, treatments, dateRange) {
    return async (dispatch) => {
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setTreatmentStatusSummaryLoading(true));

        try {
            const states = ['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'];
            const baseUrl = `/api/v1/practices/${practiceId}/reports/leads/`;
            const groupBy = 'group_by[lead_treatments__treatment]&group_by[lead_treatments__state_transition_logs__state]';
            const agg = 'aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price';

            const statePromises = states.map(async (state) => {
                const createdFilter = `&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`;

                let url;
                if (state === 'NEW') {
                    // Every lead starts as NEW on creation — the created_at filter alone
                    // is sufficient. Using state_transition_params for NEW misses leads
                    // that lack a proper NEW transition log entry.
                    const newGroupBy = 'group_by[lead_treatments__treatment]';
                    url = `${baseUrl}?${newGroupBy}&${agg}${createdFilter}`;
                } else {
                    const stateParams = encodeURIComponent(JSON.stringify({
                        state,
                        timestamp_gte: dateRange.start,
                        timestamp_lte: dateRange.end,
                    }));
                    url = `${baseUrl}?${groupBy}&${agg}&state_transition_params=${stateParams}${createdFilter}`;
                }

                try {
                    const response = await callApi(url);
                    const data = response?.data?.data || response?.data || [];
                    const items = Array.isArray(data) ? data : [];
                    if (state === 'NEW') {
                        // No state_transition_logs grouping — inject the state for merging
                        return items.map((item) => ({
                            ...item,
                            lead_treatments__state_transition_logs__state: 'NEW',
                        }));
                    }
                    return items.filter((item) => item.lead_treatments__state_transition_logs__state === state);
                } catch {
                    return [];
                }
            });

            const stateResults = await Promise.all(statePromises);
            const mergedData = stateResults.flat();

            const grouped = {};
            mergedData.forEach((item) => {
                const treatmentId = item.lead_treatments__treatment;
                if (!grouped[treatmentId]) grouped[treatmentId] = [];
                grouped[treatmentId].push(item);
            });

            const treatmentMap = {};
            treatments.forEach((t) => { treatmentMap[t.id] = t; });

            const validResults = Object.entries(grouped).map(([treatmentIdStr, stateItems]) => {
                const treatmentId = Number(treatmentIdStr);
                const treatment = treatmentMap[treatmentId];
                if (!treatment) return null;

                const getCount = (st) => stateItems.find(
                    (item) => item.lead_treatments__state_transition_logs__state === st
                )?.count_id || 0;

                const counts = {
                    NEW: getCount('NEW'),
                    CONSULTATION_BOOKED: getCount('CONSULTATION_BOOKED'),
                    CONSULTATION_ATTENDED: getCount('CONSULTATION_ATTENDED'),
                    TREATMENT_STARTED: getCount('TREATMENT_STARTED'),
                    CLOSED: getCount('CLOSED'),
                };

                const total = counts.NEW;

                return {
                    id: treatment.id,
                    treatmentId: treatment.id,
                    treatmentItemId: treatment.treatment_item?.id,
                    treatmentName: treatment.treatment_item?.name || 'Unknown',
                    color: treatment.colour || '#6366f1',
                    ...counts,
                    total,
                };
            }).filter((r) => r !== null && r.total > 0);

            dispatch(setTreatmentStatusSummary({
                data: validResults,
                isLoading: false,
            }));
        } catch (error) {
            dispatch(setTreatmentStatusSummary({
                data: [],
                isLoading: false,
            }));
        }
    };
}

// Fetch clinician conversion data for the Clinician Conversion Tracker
// Uses /api/v1/practices/{practice_pk}/reports/practice-user/{id}/leads/ endpoint
// Fetches lead treatments for each practitioner and aggregates by status
// Also stores lead IDs for each cell to enable click-to-view-details functionality
export function fetchClinicianConversionData(practiceIdOrIds, dateRange) {
    // NOTE: /lead-treatments-flat/ is not a reports view — it scopes by URL
    // practice_pk only (no practice__in support). For multi-practice users we
    // use the primary practice here; cross-practice aggregation of clinician
    // conversion requires either backend work on lead-treatments-flat or a
    // client-side fan-out. Current behaviour matches what was already shipped.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    // practice-user IS a reports endpoint, so it accepts practice__in and can
    // union practitioners across practices.
    const practiceUserQuery = ids.length > 1 ? `?practice__in=${ids.join(',')}` : '';
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setClinicianConversionLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // First fetch all practitioners (single-practice scope —
            // the multi-practice URL param was retired when the analytics
            // header's selector was hidden pending option D).
            const practitionersRes = await callApi(`${baseUrl}/practice-user/`);

            // Handle permission errors gracefully — user may not have access to this practice
            if (practitionersRes?.status === 403 || practitionersRes?.status === 401) {
                dispatch(setClinicianConversion({ data: [], treatments: [], teamTotals: {}, isLoading: false }));
                return;
            }

            const practitionersRaw = practitionersRes?.data;
            const allPractitioners = Array.isArray(practitionersRaw)
                ? practitionersRaw
                : (practitionersRaw?.results || []);

            // Filter to only include dentists (role === 'Practitioner')
            const practitioners = allPractitioners.filter((p) => p.role === 'Practitioner');

            if (practitioners.length === 0) {
                dispatch(setClinicianConversion({
                    data: [],
                    treatments: [],
                    teamTotals: {},
                    isLoading: false,
                }));
                return;
            }

            // Use the flat endpoint with state_transition_params for correct date filtering
            // This filters by WHEN a lead treatment actually transitioned to each status
            const practitionersToFetch = practitioners;
            const flatBaseUrl = `/api/v1/practices/${practiceId}/lead-treatments-flat`;
            const flatFields = 'fields={id,assigned_to,assignees,lead{id,first_name,last_name,created_at},treatment{treatment_item{name}},status,price,custom_price,contacted_at,leadtreatment_specification,state_transition_logs,appointments{assignees}}';

            // Fetch leads that transitioned to each status within the date range.
            // IMPORTANT: We do NOT filter by current status — a lead attended in Feb
            // that has since moved to TREATMENT_STARTED should still count as "attended"
            // for the practitioner who saw them. state_transition_params alone handles
            // the date filtering.
            const statusQueries = [
                { state: 'CONSULTATION_BOOKED' },
                { state: 'CONSULTATION_ATTENDED' },
                { state: 'TREATMENT_STARTED' },
                { state: 'CLOSED' },
            ];

            const statusPromises = statusQueries.map(({ state }) => {
                const stateParams = encodeURIComponent(JSON.stringify({
                    state,
                    timestamp_gte: dateRange.start,
                    timestamp_lte: dateRange.end,
                }));
                return callApi(
                    `${flatBaseUrl}/?state_transition_params=${stateParams}&${flatFields}&ordering=-created_at&limit=1000`
                ).catch(() => ({ data: { results: [] } }));
            });

            const statusResponses = await Promise.all(statusPromises);

            // Parse responses: [bookedItems, attendedItems, startedItems, closedItems]
            const parsedResponses = statusResponses.map((res) => {
                const raw = res?.data;
                return Array.isArray(raw) ? raw : (raw?.results || []);
            });
            const [bookedItems, attendedItems, startedItems, closedItems] = parsedResponses;

            // Build practice_user ID -> practitioner index map for attribution
            const puIdToIdx = {};
            practitionersToFetch.forEach((p, idx) => { puIdToIdx[p.id] = idx; });
            // Also map by user ID for appointment assignees
            const userIdToPuId = {};
            practitionersToFetch.forEach((p) => {
                if (p.user?.id) userIdToPuId[p.user.id] = p.id;
            });

            // Helper: get practitioner indices from a lead treatment record
            const getPracIndices = (lt) => {
                const indices = new Set();
                // Check assigned_to (may be integer practice_user_id or object)
                if (lt.assigned_to) {
                    const puId = typeof lt.assigned_to === 'object' ? lt.assigned_to.id : lt.assigned_to;
                    if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
                }
                // Check assignees array
                if (lt.assignees?.length > 0) {
                    lt.assignees.forEach((a) => {
                        const puId = typeof a === 'object' ? a.id : a;
                        if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
                    });
                }
                // Check appointment assignees
                if (lt.appointments?.length > 0) {
                    lt.appointments.forEach((appt) => {
                        if (appt.assignees?.length > 0) {
                            appt.assignees.forEach((a) => {
                                const puId = typeof a === 'object' ? a.id : a;
                                if (puIdToIdx[puId] !== undefined) indices.add(puIdToIdx[puId]);
                                // Also try matching via user ID
                                const userId = typeof a === 'object' ? a.user?.id || a.user : null;
                                if (userId && userIdToPuId[userId] !== undefined) {
                                    indices.add(puIdToIdx[userIdToPuId[userId]]);
                                }
                            });
                        }
                    });
                }
                return indices;
            };

            // Helper: extract treatment name from flat endpoint format
            const getTreatmentName = (lt) => {
                if (typeof lt.treatment === 'object') {
                    return lt.treatment?.treatment_item?.name || lt.treatment?.name || 'Other';
                }
                return lt.treatment || 'Other';
            };

            // Parse DD-MM-YYYY HH:mm:ss or ISO format to Date object
            const parseTimestamp = (str) => {
                if (!str) return null;
                const parts = str.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (parts) return new Date(parts[3], parts[2] - 1, parts[1], parts[4], parts[5], parts[6]);
                const d = new Date(str);
                return Number.isNaN(d.getTime()) ? null : d;
            };

            // Helper: get the transition timestamp for a specific state from state_transition_logs
            const getTransitionTimestamp = (lt, targetState) => {
                if (!lt.state_transition_logs?.length) return '';
                // Find the most recent transition to the target state
                const transition = lt.state_transition_logs
                    .filter((log) => log.state === targetState)
                    .sort((a, b) => (parseTimestamp(b.timestamp) || 0) - (parseTimestamp(a.timestamp) || 0))[0];
                return transition?.timestamp || '';
            };

            // Helper: build lead detail for popup
            const getLeadDetail = (lt, queryState) => {
                const leadObj = typeof lt.lead === 'object' ? lt.lead : {};
                // Use the transition timestamp for the queried state (e.g., when it became TREATMENT_STARTED)
                // Falls back to lead created_at if no transition log available
                const transitionDate = getTransitionTimestamp(lt, queryState);
                return {
                    leadId: leadObj.id || lt.lead,
                    leadName: `${leadObj.first_name || ''} ${leadObj.last_name || ''}`.trim() || 'Unknown',
                    treatment: getTreatmentName(lt),
                    status: lt.status,
                    price: parseFloat(lt.custom_price || lt.price || 0),
                    teethCount: lt.leadtreatment_specification?.teeth_count || 0,
                    createdAt: transitionDate || leadObj.created_at || lt.contacted_at || '',
                };
            };

            const initMetrics = () => ({
                leads: 0,
                consultationsBooked: 0, consultationsBookedDetails: [],
                consultationsAttended: 0, consultationsAttendedDetails: [],
                treatmentStarted: 0, treatmentStartedDetails: [],
                revenue: 0, teethCount: 0,
            });

            // Initialize practitioner data
            const practitionerData = practitionersToFetch.map((p) => ({
                id: p.id,
                userId: p.user?.id,
                name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || 'Unknown',
                avatar: p.user?.avatar,
                email: p.user?.email,
                treatments: {},
                totals: initMetrics(),
                _seenLeadIds: new Set(), // Track unique leads across status queries
                _seenTreatmentLeadIds: {}, // Track unique leads per treatment
            }));

            // "Unassigned" catch-all for leads not attributed to any Practitioner
            const unassignedData = {
                id: 'unassigned',
                userId: null,
                name: 'Unassigned',
                avatar: null,
                email: null,
                treatments: {},
                totals: initMetrics(),
                _seenLeadIds: new Set(),
                _seenTreatmentLeadIds: {},
                _isUnassigned: true,
            };

            const treatmentNames = new Set();

            // Helper to add a lead treatment to a practitioner's metrics
            const addToMetrics = (pracData, tName, detail, metricKeys) => {
                if (!pracData.treatments[tName]) pracData.treatments[tName] = initMetrics();
                const td = pracData.treatments[tName];
                metricKeys.forEach((mk) => {
                    if (mk === 'consultationsBooked') {
                        td.consultationsBooked += 1;
                        td.consultationsBookedDetails.push(detail);
                        pracData.totals.consultationsBooked += 1;
                        pracData.totals.consultationsBookedDetails.push(detail);
                    } else if (mk === 'consultationsAttended') {
                        td.consultationsAttended += 1;
                        td.consultationsAttendedDetails.push(detail);
                        pracData.totals.consultationsAttended += 1;
                        pracData.totals.consultationsAttendedDetails.push(detail);
                    } else if (mk === 'treatmentStarted') {
                        const price = detail.price || 0;
                        const teethCount = detail.teethCount || 0;
                        td.treatmentStarted += 1;
                        td.treatmentStartedDetails.push(detail);
                        td.revenue += price;
                        pracData.totals.treatmentStarted += 1;
                        pracData.totals.treatmentStartedDetails.push(detail);
                        pracData.totals.revenue += price;
                        if (teethCount > 0) {
                            td.teethCount += teethCount;
                            pracData.totals.teethCount += teethCount;
                        }
                    }
                });
                // Deduplicate lead counts — a lead appearing in multiple status queries
                // (e.g., booked + attended + started all in this date range) should only count once
                const leadKey = `${detail.leadId}-${tName}`;
                if (!pracData._seenTreatmentLeadIds[tName]) pracData._seenTreatmentLeadIds[tName] = new Set();
                if (!pracData._seenTreatmentLeadIds[tName].has(leadKey)) {
                    pracData._seenTreatmentLeadIds[tName].add(leadKey);
                    td.leads += 1;
                }
                if (!pracData._seenLeadIds.has(detail.leadId)) {
                    pracData._seenLeadIds.add(detail.leadId);
                    pracData.totals.leads += 1;
                }
            };

            // Client-side date validation: verify transition actually happened within date range
            // This catches edge cases where the API may return stale results
            const rangeStart = new Date(dateRange.start);
            const rangeEnd = new Date(dateRange.end);
            const isTransitionInRange = (lt, targetState) => {
                const ts = getTransitionTimestamp(lt, targetState);
                if (!ts) return true; // If no logs, trust the API filter
                const transDate = parseTimestamp(ts);
                if (!transDate) return true; // If unparseable, trust the API filter
                return transDate >= rangeStart && transDate <= rangeEnd;
            };

            // Deduplicate across responses: since we no longer filter by current status,
            // a lead that went booked→attended→started in one period appears in multiple
            // response arrays. We track each lead-treatment by ID and keep only its
            // highest funnel stage to avoid double-counting.
            //
            // Funnel priority (highest wins):
            //   4 = CLOSED      → counts: booked + attended
            //   3 = STARTED     → counts: booked + attended + started
            //   2 = ATTENDED    → counts: booked + attended
            //   1 = BOOKED      → counts: booked
            const FUNNEL_PRIORITY = { CONSULTATION_BOOKED: 1, CONSULTATION_ATTENDED: 2, TREATMENT_STARTED: 3, CLOSED: 4 };
            const FUNNEL_METRICS = {
                CONSULTATION_BOOKED: ['consultationsBooked'],
                CONSULTATION_ATTENDED: ['consultationsBooked', 'consultationsAttended'],
                TREATMENT_STARTED: ['consultationsBooked', 'consultationsAttended', 'treatmentStarted'],
                CLOSED: ['consultationsBooked', 'consultationsAttended'],
            };

            // Build dedupe map: ltId -> { lt, highestState, detail }
            const dedupeMap = new Map();
            const processItems = (items, queryState) => {
                items.filter((lt) => isTransitionInRange(lt, queryState)).forEach((lt) => {
                    const ltId = lt.id;
                    const existing = dedupeMap.get(ltId);
                    const priority = FUNNEL_PRIORITY[queryState] || 0;
                    if (!existing || priority > existing.priority) {
                        dedupeMap.set(ltId, {
                            lt,
                            highestState: queryState,
                            priority,
                            detail: getLeadDetail(lt, queryState),
                        });
                    }
                });
            };

            processItems(bookedItems, 'CONSULTATION_BOOKED');
            processItems(attendedItems, 'CONSULTATION_ATTENDED');
            processItems(startedItems, 'TREATMENT_STARTED');
            processItems(closedItems, 'CLOSED');

            // Now process each unique lead-treatment once at its highest funnel stage
            dedupeMap.forEach(({ lt, highestState, detail }) => {
                const indices = getPracIndices(lt);
                const tName = getTreatmentName(lt);
                const metricKeys = FUNNEL_METRICS[highestState] || ['consultationsBooked'];
                treatmentNames.add(tName);
                if (indices.size === 0) {
                    addToMetrics(unassignedData, tName, detail, metricKeys);
                } else {
                    indices.forEach((idx) => addToMetrics(practitionerData[idx], tName, detail, metricKeys));
                }
            });

            // Build treatment list from collected names
            const treatmentList = Array.from(treatmentNames).map((tName) => ({
                id: tName,
                name: tName,
                color: '#6366f1',
            }));

            // If no treatments found, use "All Treatments" as default
            if (treatmentList.length === 0) {
                treatmentList.push({ id: 'all', name: 'All Treatments', color: '#6366f1' });
            }

            // Append "Unassigned" row if it captured any leads
            if (unassignedData._seenLeadIds.size > 0) {
                practitionerData.push(unassignedData);
            }

            // Calculate rates and format final data
            const processedData = practitionerData
                .map((p) => {
                    const showUpRate = p.totals.consultationsBooked > 0
                        ? ((p.totals.consultationsAttended / p.totals.consultationsBooked) * 100).toFixed(1)
                        : '0';
                    const conversionRate = p.totals.consultationsAttended > 0
                        ? ((p.totals.treatmentStarted / p.totals.consultationsAttended) * 100).toFixed(1)
                        : '0';

                    // Calculate rates for each treatment
                    const treatmentsWithRates = {};
                    Object.entries(p.treatments).forEach(([tName, data]) => {
                        const catShowUpRate = data.consultationsBooked > 0
                            ? ((data.consultationsAttended / data.consultationsBooked) * 100).toFixed(1)
                            : '0';
                        const catConversionRate = data.consultationsAttended > 0
                            ? ((data.treatmentStarted / data.consultationsAttended) * 100).toFixed(1)
                            : '0';
                        treatmentsWithRates[tName] = {
                            ...data,
                            showUpRate: catShowUpRate,
                            conversionRate: catConversionRate,
                        };
                    });

                    // Strip temporary tracking properties before storing in Redux
                    const { _seenLeadIds, _seenTreatmentLeadIds, ...cleanP } = p;
                    return {
                        ...cleanP,
                        treatments: treatmentsWithRates,
                        totals: {
                            ...p.totals,
                            showUpRate,
                            conversionRate,
                        },
                    };
                })
                .filter((p) => p.totals.leads > 0)
                .sort((a, b) => {
                    // Keep "Unassigned" row at the bottom
                    if (a._isUnassigned) return 1;
                    if (b._isUnassigned) return -1;
                    return b.totals.revenue - a.totals.revenue;
                });

            // Calculate team totals
            const teamTotals = {
                leads: 0,
                consultationsBooked: 0,
                consultationsAttended: 0,
                treatmentStarted: 0,
                revenue: 0,
            };
            processedData.forEach((p) => {
                teamTotals.leads += p.totals.leads;
                teamTotals.consultationsBooked += p.totals.consultationsBooked;
                teamTotals.consultationsAttended += p.totals.consultationsAttended;
                teamTotals.treatmentStarted += p.totals.treatmentStarted;
                teamTotals.revenue += p.totals.revenue;
            });
            teamTotals.showUpRate = teamTotals.consultationsBooked > 0
                ? ((teamTotals.consultationsAttended / teamTotals.consultationsBooked) * 100).toFixed(1)
                : '0';
            teamTotals.conversionRate = teamTotals.consultationsAttended > 0
                ? ((teamTotals.treatmentStarted / teamTotals.consultationsAttended) * 100).toFixed(1)
                : '0';

            dispatch(setFetchKey({ key: '_clinicianFetchKey', value: `${practiceId}|${dateRange.start}|${dateRange.end}` }));
            dispatch(setClinicianConversion({
                data: processedData,
                treatments: treatmentList,
                teamTotals,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch clinician conversion data', error);
            dispatch(setClinicianConversion({
                data: [],
                treatments: [],
                teamTotals: {},
                isLoading: false,
            }));
            dispatch(updateError({ message: 'Failed to load clinician conversion data', success: false }));
        }
    };
}

// Fetch TCO/User performance data for the TCO Performance Tracker
// Shows TCOs and Users (non-dentists) with their booking performance
// Also stores lead IDs for each metric to enable click-to-view-details functionality
export function fetchTcoPerformanceData(practiceIdOrIds, dateRange) {
    // NOTE: per-user drilldown (/practice-user/{id}/leads/) is scoped to a
    // single practice. For multi-practice users we fetch the primary practice's
    // TCO/User leads; practice-user list union across practices is handled via
    // practice__in. Full cross-practice TCO metrics need lead-level aggregation
    // that doesn't exist on this endpoint yet.
    const ids = toPracticeIds(practiceIdOrIds);
    const practiceId = ids[0];
    const practiceUserQuery = ids.length > 1 ? `?practice__in=${ids.join(',')}` : '';
    return async (dispatch) => {
        if (!practiceId) return;
        if (!hasFeatureAccess('analytics-reports')) return;
        dispatch(setTcoPerformanceLoading(true));
        const baseUrl = `/api/v1/practices/${practiceId}/reports`;

        try {
            // Fetch all practice users (single-practice scope — the
            // practice__in query param was retired when the analytics
            // header's selector was hidden pending option D).
            const practitionersRes = await callApi(`${baseUrl}/practice-user/`);

            // Handle permission errors gracefully — user may not have access to this practice
            if (practitionersRes?.status === 403 || practitionersRes?.status === 401) {
                dispatch(setTcoPerformance({ data: [], teamTotals: {}, isLoading: false }));
                return;
            }

            const practitionersRaw = practitionersRes?.data;
            const allPractitioners = Array.isArray(practitionersRaw)
                ? practitionersRaw
                : (practitionersRaw?.results || []);

            // Filter to only include TCOs and Users (not Practitioners/Dentists)
            const tcoUsers = allPractitioners.filter((p) =>
                p.role === 'TCO' || p.role === 'USER' || p.role === 'ADMIN'
            );

            if (tcoUsers.length === 0) {
                dispatch(setTcoPerformance({
                    data: [],
                    teamTotals: {},
                    isLoading: false,
                }));
                return;
            }

            // Fetch leads for each TCO/User in parallel (limit to first 20)
            const usersToFetch = tcoUsers.slice(0, 20);
            const leadsPromises = usersToFetch.map((p) => {
                const practiceUserId = p.id;
                return callApi(
                    `${baseUrl}/practice-user/${practiceUserId}/leads/?created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}&limit=1000`
                ).catch(() => ({ data: { results: [] } }));
            });

            // Fetch lead notes data: unique leads contacted per user + channel breakdown
            const notesCountUrl = `${baseUrl}/leads/?group_by[lead_notes__created_by]&aggregate[Count]=id&created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}`;
            const notesChannelUrl = `${baseUrl}/leads/?group_by[lead_notes__created_by][lead_notes__contacted_via]&aggregate[Count]=id&created_at__gte=${encodeURIComponent(dateRange.start)}&created_at__lte=${encodeURIComponent(dateRange.end)}`;

            const [leadsResponses, notesCountRes, notesChannelRes] = await Promise.all([
                Promise.all(leadsPromises),
                callApi(notesCountUrl).catch(() => null),
                callApi(notesChannelUrl).catch(() => null),
            ]);

            // Build mapping from user ID → practice_user ID
            const userIdToPracticeUserId = {};
            usersToFetch.forEach((p) => {
                if (p.user?.id) userIdToPracticeUserId[p.user.id] = p.id;
            });

            // Parse notes count: [{ lead_notes__created_by: 42, count_id: 15 }, ...]
            const notesCountData = notesCountRes?.data?.data || notesCountRes?.data || [];
            const notesCountByPracticeUser = {};
            if (Array.isArray(notesCountData)) {
                notesCountData.forEach((item) => {
                    const userId = item.lead_notes__created_by;
                    const count = item.count_id || 0;
                    if (userId != null) {
                        const practiceUserId = userIdToPracticeUserId[userId];
                        if (practiceUserId) {
                            notesCountByPracticeUser[practiceUserId] = (notesCountByPracticeUser[practiceUserId] || 0) + count;
                        }
                    }
                });
            }

            // Parse channel breakdown: [{ lead_notes__created_by: 42, lead_notes__contacted_via: "PHONE", count_id: 8 }, ...]
            const notesChannelData = notesChannelRes?.data?.data || notesChannelRes?.data || [];
            const contactMethodsByPracticeUser = {};
            if (Array.isArray(notesChannelData)) {
                notesChannelData.forEach((item) => {
                    const userId = item.lead_notes__created_by;
                    const channel = item.lead_notes__contacted_via;
                    const count = item.count_id || 0;
                    if (userId != null && channel) {
                        const practiceUserId = userIdToPracticeUserId[userId];
                        if (practiceUserId) {
                            if (!contactMethodsByPracticeUser[practiceUserId]) {
                                contactMethodsByPracticeUser[practiceUserId] = {};
                            }
                            contactMethodsByPracticeUser[practiceUserId][channel] = (contactMethodsByPracticeUser[practiceUserId][channel] || 0) + count;
                        }
                    }
                });
            }

            // Process lead data for each TCO/User
            const userData = [];

            usersToFetch.forEach((user, index) => {
                const practiceUserId = user.id;
                const name = `${user.user?.first_name || ''} ${user.user?.last_name || ''}`.trim() || 'Unknown';
                const leadsRaw = leadsResponses[index]?.data;
                const leadTreatments = Array.isArray(leadsRaw) ? leadsRaw : (leadsRaw?.results || []);

                // Initialize user stats with lead ID arrays
                const userStats = {
                    id: practiceUserId,
                    name,
                    role: user.role,
                    avatar: user.user?.avatar,
                    email: user.user?.email,
                    // Metrics for TCOs with lead IDs
                    patientsContacted: 0,
                    patientsContactedIds: [],
                    consultationsBooked: 0,
                    consultationsBookedIds: [],
                    consultationsAttended: 0,
                    consultationsAttendedIds: [],
                    treatmentStarted: 0,
                    treatmentStartedIds: [],
                    revenue: 0,
                    // Contact activity from lead notes
                    leadsContacted: notesCountByPracticeUser[practiceUserId] || 0,
                    contactMethods: contactMethodsByPracticeUser[practiceUserId] || {},
                };

                // Process each lead treatment
                leadTreatments.forEach((lt) => {
                    const {status} = lt;
                    const price = parseFloat(lt.custom_price || lt.price || 0);
                    const leadId = lt.lead?.id || lt.lead_id;

                    // Count as patient contacted (any lead assigned)
                    userStats.patientsContacted += 1;
                    if (leadId && !userStats.patientsContactedIds.includes(leadId)) {
                        userStats.patientsContactedIds.push(leadId);
                    }

                    // CONSULTATION_BOOKED or further = patient booked in
                    if (['CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'].includes(status)) {
                        userStats.consultationsBooked += 1;
                        if (leadId && !userStats.consultationsBookedIds.includes(leadId)) {
                            userStats.consultationsBookedIds.push(leadId);
                        }
                    }

                    // CONSULTATION_ATTENDED or further
                    if (['CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'].includes(status)) {
                        userStats.consultationsAttended += 1;
                        if (leadId && !userStats.consultationsAttendedIds.includes(leadId)) {
                            userStats.consultationsAttendedIds.push(leadId);
                        }
                    }

                    // Only TREATMENT_STARTED counts toward treatmentStarted
                    // CLOSED leads already completed — don't inflate the started count
                    if (status === 'TREATMENT_STARTED') {
                        userStats.treatmentStarted += 1;
                        userStats.revenue += price;
                        if (leadId && !userStats.treatmentStartedIds.includes(leadId)) {
                            userStats.treatmentStartedIds.push(leadId);
                        }
                    }

                    // CLOSED leads still count their revenue
                    if (status === 'CLOSED') {
                        userStats.revenue += price;
                    }
                });

                userData.push(userStats);
            });

            // Calculate rates and format final data
            const processedData = userData
                .map((u) => {
                    const bookingRate = u.patientsContacted > 0
                        ? ((u.consultationsBooked / u.patientsContacted) * 100).toFixed(1)
                        : '0';
                    const showUpRate = u.consultationsBooked > 0
                        ? ((u.consultationsAttended / u.consultationsBooked) * 100).toFixed(1)
                        : '0';
                    const conversionRate = u.consultationsAttended > 0
                        ? ((u.treatmentStarted / u.consultationsAttended) * 100).toFixed(1)
                        : '0';

                    return {
                        ...u,
                        bookingRate,
                        showUpRate,
                        conversionRate,
                    };
                })
                .filter((u) => u.patientsContacted > 0)
                .sort((a, b) => b.consultationsBooked - a.consultationsBooked);

            // Calculate team totals
            const teamTotals = {
                patientsContacted: 0,
                consultationsBooked: 0,
                consultationsAttended: 0,
                treatmentStarted: 0,
                revenue: 0,
                leadsContacted: 0,
            };
            processedData.forEach((u) => {
                teamTotals.patientsContacted += u.patientsContacted;
                teamTotals.consultationsBooked += u.consultationsBooked;
                teamTotals.consultationsAttended += u.consultationsAttended;
                teamTotals.treatmentStarted += u.treatmentStarted;
                teamTotals.revenue += u.revenue;
                teamTotals.leadsContacted += u.leadsContacted;
            });
            teamTotals.bookingRate = teamTotals.patientsContacted > 0
                ? ((teamTotals.consultationsBooked / teamTotals.patientsContacted) * 100).toFixed(1)
                : '0';
            teamTotals.showUpRate = teamTotals.consultationsBooked > 0
                ? ((teamTotals.consultationsAttended / teamTotals.consultationsBooked) * 100).toFixed(1)
                : '0';
            teamTotals.conversionRate = teamTotals.consultationsAttended > 0
                ? ((teamTotals.treatmentStarted / teamTotals.consultationsAttended) * 100).toFixed(1)
                : '0';

            dispatch(setFetchKey({ key: '_tcoFetchKey', value: `${practiceId}|${dateRange.start}|${dateRange.end}` }));
            dispatch(setTcoPerformance({
                data: processedData,
                teamTotals,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Failed to fetch TCO performance data', error);
            dispatch(setTcoPerformance({
                data: [],
                teamTotals: {},
                isLoading: false,
            }));
            dispatch(updateError({ message: 'Failed to load TCO performance data', success: false }));
        }
    };
}

export const fetchLostLeadsData = (practiceIds, dateRange) => async (dispatch) => {
    const ids = toPracticeIds(practiceIds);
    if (ids.length === 0) return;
    if (!hasFeatureAccess('analytics-reports')) return;
    try {
        dispatch(setLostLeadsLoading(true));
        const startDate = dateRange.start?.split(' ')[0] || dateRange.start;
        const endDate = dateRange.end?.split(' ')[0] || dateRange.end;

        // Previous period for MoM (equal-length window ending 1s before current start)
        const start = new Date(startDate);
        const end = new Date(endDate);
        const periodMs = end.getTime() - start.getTime();
        const prevEnd = new Date(start.getTime() - 1);
        const prevStart = new Date(prevEnd.getTime() - periodMs);
        const prevStartStr = prevStart.toISOString().split('T')[0];
        const prevEndStr = prevEnd.toISOString().split('T')[0];
        const prevRange = { start: `${prevStartStr} 00:00:00`, end: `${prevEndStr} 23:59:59` };

        // Multi-practice strategy:
        //   - `/reports/*` endpoints accept practice__in → one request aggregates
        //     across all selected practices. Used for count/byReason/byTreatment/bySource.
        //   - `/archived/?limit=50` is under apps/leads/views.py (not a report view)
        //     and scopes by URL practice_pk only — we still fan out for the sample
        //     list. That's fine: it's 1 paginated request per practice, not 6.
        //   - summary endpoint for COUNT: practice__in is respected there too.
        const pSuffix = practiceInSuffix(ids);
        const primaryId = ids[0];
        const baseUrl = `/api/v1/practices/${primaryId}/reports`;

        // Partial render: allSettled so one failed sub-section doesn't blank
        // the whole Lost Leads page. Each piece is defaulted to a safe empty
        // value (0 / {}) and the thunk proceeds; sections that succeeded
        // render as usual, sections that failed show as "0" / empty.
        const settled = await Promise.allSettled([
            fetchArchivedCountForPeriod(ids, dateRange),
            fetchArchivedCountForPeriod(ids, prevRange),
            callApi(`${baseUrl}/leads/?aggregate[Count]=id&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            callApi(`${baseUrl}/leads/?group_by[lead_notes__note]&aggregate[Count]=id&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            callApi(`${baseUrl}/leads/?group_by[lead_treatments__treatment__name]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            callApi(`${baseUrl}/leads/?group_by[lead_source__type]&aggregate[Count]=id&is_archived=true&created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}${pSuffix}`),
            Promise.allSettled(ids.map((practiceId) => callApi(`/api/v1/practices/${practiceId}/archived/?is_archived=true&limit=50`))),
        ]);
        const pick = (result, fallback) => (result.status === 'fulfilled' ? result.value : fallback);
        const currentArchivedCount = pick(settled[0], 0);
        const prevArchivedCount = pick(settled[1], 0);
        const totalLeadsRes = pick(settled[2], { data: { data: [] } });
        const byReasonRes = pick(settled[3], { data: { data: [] } });
        const byTreatmentRes = pick(settled[4], { data: { data: [] } });
        const bySourceRes = pick(settled[5], { data: { data: [] } });
        // archivedSample itself is allSettled, so unwrap each practice's
        // response independently — one practice failing shouldn't blank the
        // sample table.
        const archivedSampleResults = pick(settled[6], [])
            .map((r) => (r.status === 'fulfilled' ? r.value : null))
            .filter(Boolean);

        // Report any section failures to the console so support can diagnose
        // without the user ever seeing a silent blank section.
        const failedSections = ['currentCount', 'prevCount', 'totalLeads', 'byReason', 'byTreatment', 'bySource', 'archivedSample']
            .filter((_, i) => settled[i].status === 'rejected');
        if (failedSections.length > 0) {
            // eslint-disable-next-line no-console
            console.warn('[lost-leads] partial render, failed sections:', failedSections);
        }

        // The /archived/ endpoint silently ignores every date filter we've tested,
        // so it returns a date-agnostic sample. Filter client-side by archived_at
        // against the selected dateRange — otherwise a 24-hour window (widget=1)
        // would show all 50 rows of the sample in the table below.
        const rangeStartMs = dayjs(dateRange.start, API_DATE_FORMATS).valueOf();
        const rangeEndMs = dayjs(dateRange.end, API_DATE_FORMATS).valueOf();
        const archivedLeadsAll = archivedSampleResults.flatMap((res) => res?.data?.results || []);
        const archivedLeads = (!Number.isNaN(rangeStartMs) && !Number.isNaN(rangeEndMs))
            ? archivedLeadsAll.filter((lead) => {
                const parsed = parseApiDate(lead.archived_at || lead.created_at);
                if (!parsed?.isValid()) return false;
                const ms = parsed.valueOf();
                return ms >= rangeStartMs && ms <= rangeEndMs;
            })
            : archivedLeadsAll;
        const totalLeadsRaw = totalLeadsRes?.data?.data;
        const totalLeadsCount = (Array.isArray(totalLeadsRaw) && totalLeadsRaw[0]?.count_id)
            ? totalLeadsRaw[0].count_id
            : (totalLeadsRes?.data?.count ?? 0);
        const byReasonRaw = byReasonRes?.data?.data || [];
        const byTreatmentRaw = byTreatmentRes?.data?.data || [];
        const bySourceRaw = bySourceRes?.data?.data || [];

        const VALID_ARCHIVE_REASONS_SET = new Set(VALID_ARCHIVE_REASONS);

        // byReason — merge server-side group_by aggregates across practices
        const reasonMap = {};
        byReasonRaw.forEach((item) => {
            const reason = item.lead_notes__note;
            if (!reason || !VALID_ARCHIVE_REASONS_SET.has(reason)) return;
            reasonMap[reason] = (reasonMap[reason] || 0) + (item.count_id || 0);
        });
        const byReason = Object.entries(reasonMap)
            .map(([reason, count]) => ({ reason, label: formatArchiveReasonLabel(reason), count }))
            .sort((a, b) => b.count - a.count);

        // byTreatment — merge across practices; `sum_lead_treatments__custom_price`
        // is the backend's aggregate revenue per treatment name
        const treatmentMap = {};
        byTreatmentRaw.forEach((item) => {
            const name = item.lead_treatments__treatment__name;
            if (!name) return;
            if (!treatmentMap[name]) treatmentMap[name] = { count: 0, revenue: 0 };
            treatmentMap[name].count += item.count_id || 0;
            treatmentMap[name].revenue += item.sum_lead_treatments__custom_price || 0;
        });
        const byTreatment = Object.entries(treatmentMap)
            .map(([name, v]) => ({ name, count: v.count, revenue: v.revenue }))
            .sort((a, b) => b.count - a.count);

        // bySource — merge across practices
        const sourceMap = {};
        bySourceRaw.forEach((item) => {
            const type = item.lead_source__type;
            if (!type) return;
            sourceMap[type] = (sourceMap[type] || 0) + (item.count_id || 0);
        });
        const bySource = Object.entries(sourceMap)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count);

        // Fix #6: Build byTeamMember from archived leads assigned_to field
        const teamMemberMap = {};
        archivedLeads.forEach((lead) => {
            const assignee = lead.assigned_to;
            if (assignee) {
                const name = assignee.full_name || `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || 'Unknown';
                const key = assignee.id || name;
                if (!teamMemberMap[key]) {
                    teamMemberMap[key] = { name, count: 0, reasons: {} };
                }
                teamMemberMap[key].count += 1;
                // Find valid archive reason from notes first (matches API grouping), then fields
                const noteReason = lead.lead_notes?.find((n) => VALID_ARCHIVE_REASONS_SET.has(n.note))?.note;
                const reason = noteReason || lead.reason_for_archiving || lead.archived_reason;
                if (reason) {
                    teamMemberMap[key].reasons[reason] = (teamMemberMap[key].reasons[reason] || 0) + 1;
                }
            }
        });
        const byTeamMember = Object.values(teamMemberMap)
            .map((member) => {
                // Find the most common reason for this team member
                let topReason = '-';
                let topCount = 0;
                Object.entries(member.reasons).forEach(([reason, count]) => {
                    if (count > topCount) {
                        topCount = count;
                        topReason = formatArchiveReasonLabel(reason);
                    }
                });
                return { name: member.name, count: member.count, topReason };
            })
            .sort((a, b) => b.count - a.count);

        // Build detailed leads list — Fix #3: use archived_at for date
        const detailedLeads = archivedLeads.map((lead) => ({
            id: lead.id,
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
            treatment: lead.lead_treatments?.[0]?.treatment?.name || lead.lead_treatments?.[0]?.treatment_name || '-',
            source: lead.lead_source?.type || '-',
            reason: lead.lead_notes?.find((n) => VALID_ARCHIVE_REASONS_SET.has(n.note))?.note || lead.reason_for_archiving || lead.archived_reason || '-',
            date: lead.archived_at || lead.created_at || null,
            value: lead.lead_treatments?.reduce((sum, t) => sum + (t.custom_price || 0), 0) || 0,
            assignedTo: lead.assigned_to?.full_name || `${lead.assigned_to?.first_name || ''} ${lead.assigned_to?.last_name || ''}`.trim() || '-',
        }));

        // Use summary API count (most accurate — not capped by limit), fallback to byReason sum, then archivedLeads.length
        const byReasonTotal = byReason.reduce((sum, r) => sum + r.count, 0);
        const totalLost = currentArchivedCount || byReasonTotal || archivedLeads.length;
        const topReasonItem = byReason[0];
        const lostRevenue = byTreatment.reduce((sum, t) => sum + (t.revenue || 0), 0);

        // Calculate real avgDaysToArchive from archived_at - created_at
        let avgDaysToArchive = 0;
        const validDays = [];
        archivedLeads.forEach((lead) => {
            if (lead.archived_at && lead.created_at) {
                const created = parseApiDate(lead.created_at);
                const archived = parseApiDate(lead.archived_at);
                if (created?.isValid() && archived?.isValid()) {
                    const diffDays = Math.max(0, archived.diff(created, 'day'));
                    validDays.push(diffDays);
                }
            }
        });
        if (validDays.length > 0) {
            avgDaysToArchive = Math.round(validDays.reduce((sum, d) => sum + d, 0) / validDays.length);
        }

        // Generate trend data using archived_at (when lead was lost)
        const trendMap = {};
        archivedLeads.forEach((lead) => {
            const dateField = lead.archived_at || lead.created_at;
            const parsed = parseApiDate(dateField);
            if (parsed?.isValid()) {
                const month = parsed.format('YYYY-MM');
                trendMap[month] = (trendMap[month] || 0) + 1;
            }
        });
        const trend = Object.entries(trendMap)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, count]) => ({
                month,
                label: dayjs(month, 'YYYY-MM').format('MMM YY'),
                count,
            }));

        // Fix #9: Archive rate = archived / total leads in period.
        // When the totalLeads call itself rejected (Promise.allSettled
        // partial failure), totalLeadsCount falls back to 0 — which would
        // render "0% archive rate" even when currentArchivedCount > 0,
        // misleading the user into thinking nothing was archived. Surface
        // null in that case so the UI can render "—"/"N/A" instead.
        const totalLeadsFailed = settled[2]?.status === 'rejected';
        let archiveRate;
        if (totalLeadsFailed && currentArchivedCount > 0) {
            archiveRate = null;
        } else if (totalLeadsCount > 0) {
            archiveRate = parseFloat(((currentArchivedCount / totalLeadsCount) * 100).toFixed(1));
        } else {
            archiveRate = 0;
        }

        // Fix #7: MoM comparison
        let momChange = null;
        if (prevArchivedCount > 0) {
            const change = currentArchivedCount - prevArchivedCount;
            const pct = ((change / prevArchivedCount) * 100).toFixed(1);
            let direction = 'flat';
            if (change > 0) direction = 'up';
            else if (change < 0) direction = 'down';
            momChange = {
                count: change,
                pct: `${change >= 0 ? '+' : ''}${pct}%`,
                direction,
            };
        } else if (currentArchivedCount > 0) {
            momChange = { count: currentArchivedCount, pct: 'New', direction: 'up' };
        }

        dispatch(setLostLeadsData({
            kpis: {
                totalLost,
                topReason: topReasonItem?.label || 'N/A',
                avgDaysToArchive,
                lostRevenue,
                archiveRate,
                momChange,
            },
            byReason,
            byTreatment,
            bySource,
            byTeamMember,
            trend,
            detailedLeads,
        }));
    } catch (error) {
        console.error('Failed to fetch lost leads data', error);
        dispatch(setLostLeadsLoading(false));
    }
};

// ─── Click Tracking helpers ───────────────────────────────────────────────────

/**
 * Derives a human-readable page label from a raw pathname.
 * Strips query strings and hash fragments before splitting to avoid leaking
 * PII or session tokens that may appear in URL params.
 * e.g. "/treatments/invisalign?ref=ad" → "Treatments Page"
 *      "/"                             → "Home Page"
 *      ""  / null                      → "Home Page"
 */
export function getPageLabel(rawPathname) {
    if (!rawPathname) return 'Home Page';
    // Strip query string and hash fragment (PII / redirect risk)
    const clean = rawPathname.split('?')[0].split('#')[0];
    const segment = clean.split('/')?.[1] || '';
    if (!segment) return 'Home Page';
    const titled = segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    return `${titled} Page`;
}

/**
 * Extracts a human-readable traffic source label from the full page URL.
 * Priority: gclid/gad_source → Google Ads, fbclid → Facebook Ads, utm_source → mapped/raw, else Direct/Organic.
 */
function getTrafficSource(currentUrl) {
    if (typeof currentUrl !== 'string' || !currentUrl) return 'Direct / Organic';
    try {
        const url = new URL(currentUrl);
        const params = new URLSearchParams(url.search);
        const pathname = (url.pathname || '').toLowerCase();

        // Google Ads signals (any of these wins):
        //   - gclid / gad_source on the URL (paid click ID)
        //   - landing page slug contains /ad/ or /ads/ (dedicated PPC landing page, gclid-stripped)
        if (params.get('gclid') || params.get('gad_source')) return 'Google Ads';
        if (/\/ads?\//.test(pathname)) return 'Google Ads';

        if (params.get('fbclid')) return 'Facebook Ads';
        const utmSource = params.get('utm_source');
        if (utmSource) {
            const map = { local: 'Local / GMB', organic: 'Organic', newsletter: 'Newsletter', email: 'Email' };
            return map[utmSource.toLowerCase()] ?? utmSource;
        }
        return 'Direct / Organic';
    } catch {
        return 'Direct / Organic';
    }
}

/** Normalises a single analytics-events API record into the canonical shape. */
/**
 * Returns a lowercase, bare hostname for any URL-ish input, or '' when input
 * doesn't parse. Strips protocol, port, `www.`, and trailing slashes so that
 * comparisons between `https://www.aurora-dental.co.uk/contact` and
 * `aurora-dental.co.uk` succeed. Used by the click-tracking cross-tenant
 * safety filter to detect events that originated on a different practice's
 * website.
 */
function normaliseHost(input) {
    if (!input || typeof input !== 'string') return '';
    const trimmed = input.trim();
    if (!trimmed) return '';
    // Accept both bare hostnames ("aurora-dental.co.uk") and full URLs
    // ("https://www.aurora-dental.co.uk/contact"). URL() throws on bare hosts,
    // so prefix a scheme as a fallback.
    let host = '';
    try {
        host = new URL(trimmed).hostname;
    } catch {
        try {
            host = new URL(`https://${trimmed}`).hostname;
        } catch {
            return '';
        }
    }
    return host.toLowerCase().replace(/^www\./, '');
}

function normaliseEvent(apiRecord, type) {
    const metadata = apiRecord.metadata || {};
    const rawPath = (metadata.pathname || '/').split('?')[0].split('#')[0];

    // Timestamp resolution:
    //   1. metadata.timeStamp is ISO 8601 (e.g. "2025-06-22T09:25:23.428Z") — preferred
    //   2. created_at is DD-MM-YYYY HH:mm:ss.microsZ (NOT ISO) — parse via regex fallback
    const { iso, ms } = resolveEventTimestamp(apiRecord);

    // Capture practice scoping from whichever field the BE serializer exposes.
    // Used by the cross-tenant safety filter in fetchClickTrackingData below.
    const rawPractice = apiRecord.practice ?? apiRecord.practice_id
        ?? apiRecord.data?.practice ?? apiRecord.data?.practice_id ?? null;

    return {
        id: String(apiRecord.id ?? apiRecord.data?.id),
        type,
        practice: rawPractice == null ? null : String(rawPractice),
        occurredAt: iso,                                            // always ISO string or ''
        occurredAtMs: ms,                                           // epoch ms or NaN
        createdAtHuman: apiRecord.created_at_human ?? apiRecord.data?.created_at_human ?? '',
        pagePath: rawPath,
        pageLabel: getPageLabel(rawPath),
        deviceType: String(metadata.browserInfo || '').includes('iPhone') ? 'iPhone' : 'Browser',
        clickCount: typeof metadata.clickCount === 'number' ? metadata.clickCount : 1,
        source: getTrafficSource(metadata.currentUrl),
        currentUrl: metadata.currentUrl || '',
    };
}

function resolveEventTimestamp(apiRecord) {
    // Preferred: metadata.timeStamp is ISO 8601
    const iso1 = apiRecord?.metadata?.timeStamp;
    if (typeof iso1 === 'string' && iso1) {
        const ms = new Date(iso1).getTime();
        if (Number.isFinite(ms)) return { iso: iso1, ms };
    }
    // Fallback: parse DD-MM-YYYY HH:mm:ss(.SSSSSS)Z from created_at (top-level or nested)
    const raw = apiRecord?.created_at ?? apiRecord?.data?.created_at;
    if (typeof raw === 'string') {
        const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?Z?$/);
        if (m) {
            const [, dd, mm, yyyy, hh, mi, ss, micros] = m;
            const millis = micros ? micros.slice(0, 3).padEnd(3, '0') : '000';
            const isoStr = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.${millis}Z`;
            const ms = new Date(isoStr).getTime();
            if (Number.isFinite(ms)) return { iso: isoStr, ms };
        }
    }
    return { iso: '', ms: NaN };
}

/**
 * Computes KPIs over a merged, date-filtered event array.
 *
 * KPI values are EVENT COUNTS (not sum of clickCount).
 * Top page / top source use alphabetical tiebreaker for deterministic rendering.
 *
 * `formSubmissionsByPage` is the optional page-level aggregate (array of
 * { pagePath, submissions, ... }) so we can compute Top Converting Page
 * without the previous broken pattern of stamping ``formSubmissions`` on
 * every event row.
 *
 * Exported so ClickTracking.jsx can recompute KPIs after client-side source filtering.
 */
export function computeClickTrackingKpis(events, formSubmissionsByPage = []) {
    const phoneClicks = events.filter((e) => e.type === 'PHONE').length;
    const whatsappClicks = events.filter((e) => e.type === 'WHATSAPP').length;
    const totalClicks = events.length;

    // Most-frequent page path → human label (alphabetical tiebreaker)
    const pageFreq = {};
    events.forEach((e) => {
        pageFreq[e.pagePath] = (pageFreq[e.pagePath] || 0) + 1;
    });

    let topPage = null;
    let topPageCount = 0;
    Object.entries(pageFreq)
        .sort(([pathA], [pathB]) => pathA.localeCompare(pathB))
        .forEach(([path, cnt]) => {
            if (cnt > topPageCount) {
                topPageCount = cnt;
                topPage = getPageLabel(path);
            }
        });

    // Most-frequent source label (alphabetical tiebreaker)
    const sourceFreq = {};
    events.forEach((e) => {
        sourceFreq[e.source] = (sourceFreq[e.source] || 0) + 1;
    });

    let topSource = null;
    let topSourceCount = 0;
    Object.entries(sourceFreq)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([src, cnt]) => {
            if (cnt > topSourceCount) {
                topSourceCount = cnt;
                topSource = src;
            }
        });

    // Page with highest form submissions — read from the standalone
    // page-level aggregate (NOT per-event, which double-counted).
    let topConvertingPage = null;
    let topConvCount = 0;
    [...formSubmissionsByPage]
        .sort((a, b) => (a.pagePath || '').localeCompare(b.pagePath || '')) // alphabetical tiebreaker
        .forEach((bucket) => {
            if ((bucket.submissions || 0) > topConvCount) {
                topConvCount = bucket.submissions || 0;
                topConvertingPage = getPageLabel(bucket.pagePath) || bucket.pageLabel;
            }
        });

    return { totalClicks, phoneClicks, whatsappClicks, topPage, topSource, topConvertingPage };
}

// ─── Click Tracking thunk ─────────────────────────────────────────────────────

/**
 * Fetches phone and WhatsApp click events in parallel for the given practice
 * and date range. Uses Promise.allSettled so a single-endpoint failure still
 * renders partial data.
 *
 * Date filter strategy: client-side only.
 * The analytics-events endpoint silently ignores `created_at__gte`/`lte`
 * params (returns HTTP 200 with all events regardless). Confirmed by
 * backend-testing task #3. We therefore fetch all events and filter by date
 * after merge.
 *
 * @param {number} practiceId
 * @param {{ start: string, end: string }} dateRange  'YYYY-MM-DD HH:mm:ss' strings
 */
export function fetchClickTrackingData(practiceId, { start, end, typeFilter = 'all' } = {}) {
    return async (dispatch, getState) => {
        if (!practiceId) return;

        // typeFilter scopes the server-side fetch: 'all' hits both
        // phone + whatsapp endpoints in parallel; 'phone' or 'whatsapp'
        // hits only the matching one. Keeps the dropdown filter
        // server-side rather than dropping events client-side after
        // wasted bandwidth.
        const normalizedType = ['all', 'phone', 'whatsapp'].includes(typeFilter)
            ? typeFilter
            : 'all';
        const fetchKey = `${practiceId}|${start}|${end}|${normalizedType}`;
        const current = getState().analyticsReports.clickTracking._fetchKey;
        if (current === fetchKey) return; // already up to date

        dispatch(clickTrackingLoading());

        const base = `/api/v1/practices/${practiceId}/analytics-events/`;

        // Date-range params — backend AnalyticsEventFilter now supports
        // created_at__gte / created_at__lte (apps/analytics/filters.py).
        // Passing them server-side means we no longer fall off the
        // pagination cliff (DRF default 50/page) on high-volume
        // practices — the date toggle would otherwise be cosmetic
        // because every "Last X" range would return the same most-
        // recent 50 events.
        //
        // ``limit=5000`` is a safety upper bound — bigger than any
        // realistic per-practice click volume in a 30-day window
        // (top performers do hundreds per month, not thousands).
        // If a practice ever exceeds this, the dashboard will still
        // render — just clamped to the most recent 5000, which is
        // plenty for trend visualisation.
        const dateParams = [];
        if (start) dateParams.push(`created_at__gte=${encodeURIComponent(start)}`);
        if (end) dateParams.push(`created_at__lte=${encodeURIComponent(end)}`);
        const dateQs = dateParams.length ? `&${dateParams.join('&')}` : '';

        // Build the list of click-event fetches conditional on
        // typeFilter. The leads query is unconditional — it powers
        // attribution computations regardless of which click type is
        // shown.
        const phonePromise = (normalizedType === 'all' || normalizedType === 'phone')
            ? callApi(`${base}?name=practice_phone_number_clicked&ordering=-created_at&limit=5000${dateQs}`)
            : Promise.resolve({ ok: true, data: { results: [] } });
        const waPromise = (normalizedType === 'all' || normalizedType === 'whatsapp')
            ? callApi(`${base}?name=practice_whatsapp_button_clicked&ordering=-created_at&limit=5000${dateQs}`)
            : Promise.resolve({ ok: true, data: { results: [] } });

        const [phoneResult, waResult, leadsResult] = await Promise.allSettled([
            phonePromise,
            waPromise,
            callApi(`/api/v1/practices/${practiceId}/leads/?limit=1000&ordering=-created_at&created_after=${encodeURIComponent(start)}&created_before=${encodeURIComponent(end)}&fields={lead_source}`),
        ]);

        const phoneOk = phoneResult.status === 'fulfilled' && phoneResult.value?.ok;
        const waOk = waResult.status === 'fulfilled' && waResult.value?.ok;

        if (!phoneOk && !waOk) {
            // Both click-event endpoints failed — surface a blocking error
            const msg = phoneResult.reason?.message || waResult.reason?.message || "Couldn't load click events";
            dispatch(clickTrackingError(msg));
            return;
        }

        // Normalise whichever click-event endpoints succeeded
        const phoneEvents = phoneOk
            ? (phoneResult.value.data?.results || []).map((r) => normaliseEvent(r, 'PHONE'))
            : [];
        const waEvents = waOk
            ? (waResult.value.data?.results || []).map((r) => normaliseEvent(r, 'WHATSAPP'))
            : [];

        // Cross-tenant safety filter (defense-in-depth).
        //
        // The endpoint URL contains the practiceId, but if the BE viewset is not
        // actually scoping AnalyticsEvent queries by practice (suspected — Aurora
        // Dental users have been seeing other practices' URLs in the Page tooltip),
        // we drop any record whose practice field doesn't match the requested
        // practiceId. If the BE doesn't serialize a practice field at all we have
        // no way to verify by practice id directly, so we fall back to a hostname
        // match against the practice's registered website (PracticeContact.website)
        // and drop events whose currentUrl host doesn't match. This catches the
        // observed WhatsApp leak: practices appear to share an AnalyticsTag code
        // in the tracking snippet, so events from other practices' websites land
        // under the wrong practice tag — the originating URL is the only signal
        // we have to detect this.
        const practiceIdStr = String(practiceId);
        const merged = [...phoneEvents, ...waEvents];
        const hasPracticeField = merged.some((e) => e.practice != null);
        if (!hasPracticeField && merged.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                '[ClickTracking] AnalyticsEvent records have no practice field — falling back to currentUrl hostname match. Backend should expose tag.practice on the read serializer and reject cross-tag event creation.'
            );
        }
        const tenantSafeById = merged.filter((e) => e.practice == null || e.practice === practiceIdStr);

        // Hostname fallback — resolve the practice's registered website from the
        // already-loaded practice list and normalise to a bare hostname (strip
        // protocol, www., trailing slash).
        const practiceRow = (getState().practiceTreatmentList?.practiceTreatment?.results || [])
            .find((p) => String(p.id) === practiceIdStr);
        const expectedHost = normaliseHost(practiceRow?.contact?.website);
        const tenantSafe = expectedHost
            ? tenantSafeById.filter((e) => {
                const host = normaliseHost(e.currentUrl);
                if (!host) return true; // no URL captured → can't verify, keep
                return host === expectedHost;
            })
            : tenantSafeById;
        const droppedCount = merged.length - tenantSafe.length;
        if (droppedCount > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                `[ClickTracking] Dropped ${droppedCount} cross-tenant event(s) from analytics-events response (expected practice ${practiceIdStr}, expected host ${expectedHost || 'unknown'}). Backend tracking snippet/tag association needs audit.`
            );
        }

        // Client-side date filter — defensive second pass. The server
        // now honours ``created_at__gte`` / ``created_at__lte`` (see
        // AnalyticsEventFilter in apps/analytics/filters.py), so the
        // happy path is already filtered upstream. This local pass
        // catches edge cases: events with malformed timestamps that
        // we'd rather keep visible, off-by-one boundaries between
        // wall-clock and stored UTC, etc.
        //
        // Bug fix retained: new Date('YYYY-MM-DD HH:mm:ss') returns
        // NaN in Safari — it only parses ISO 8601 with a T separator.
        // dayjs with customParseFormat handles both space- and
        // T-separated formats so the space format moment produces in
        // ClickTracking.jsx is read cross-browser.
        const rawStartMs = start ? dayjs(start, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).valueOf() : NaN;
        const rawEndMs = end ? dayjs(end, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).valueOf() : NaN;
        const startMs = Number.isFinite(rawStartMs) ? rawStartMs : 0;
        const endMs = Number.isFinite(rawEndMs) ? rawEndMs : Infinity;
        const allEvents = tenantSafe.filter((e) => {
            if (!Number.isFinite(e.occurredAtMs)) return true; // keep events with no parseable timestamp
            return e.occurredAtMs >= startMs && e.occurredAtMs <= endMs;
        });

        // Sort merged result by occurredAt descending (most recent first)
        allEvents.sort((a, b) => (Number.isFinite(b.occurredAtMs) ? b.occurredAtMs : 0)
                               - (Number.isFinite(a.occurredAtMs) ? a.occurredAtMs : 0));

        // Build pathname → form-submission count map from ONLINE_FORM leads.
        // Only leads where lead_source.metadata.url_checker is a valid URL are counted.
        const leadsOk = leadsResult.status === 'fulfilled' && leadsResult.value?.ok;
        const formSubmissionsByPath = {};
        if (leadsOk) {
            (leadsResult.value.data?.results || []).forEach((lead) => {
                const urlChecker = lead.lead_source?.metadata?.url_checker;
                if (!urlChecker) return;
                try {
                    const pathname = new URL(urlChecker).pathname.split('?')[0].split('#')[0];
                    if (pathname) {
                        formSubmissionsByPath[pathname] = (formSubmissionsByPath[pathname] || 0) + 1;
                    }
                } catch {
                    // malformed URL — skip silently
                }
            });
        }

        // Per-page aggregate — clicks AND form submissions by pagePath.
        // Lives as derived state OUTSIDE the per-event list so the click
        // events table stays click-level and the form-submission card
        // stays page-level. They join on pagePath but are NEVER smeared
        // into the same row. (Previous behaviour stamped each event with
        // formSubmissions = pageCount, which double-counted on sum and
        // mis-suggested per-click attribution.)
        const pageBuckets = {};
        allEvents.forEach((e) => {
            const path = e.pagePath || '/';
            if (!pageBuckets[path]) {
                pageBuckets[path] = {
                    pagePath: path,
                    pageLabel: e.pageLabel || path,
                    clicks: 0,
                    submissions: 0,
                };
            }
            pageBuckets[path].clicks += 1;
        });
        // Add submissions to existing buckets, or create bucket if a page
        // has submissions but no clicks (so it still surfaces in the card).
        Object.entries(formSubmissionsByPath).forEach(([path, count]) => {
            if (!pageBuckets[path]) {
                pageBuckets[path] = {
                    pagePath: path,
                    pageLabel: path,
                    clicks: 0,
                    submissions: 0,
                };
            }
            pageBuckets[path].submissions = count;
        });
        const formSubmissionsByPage = Object.values(pageBuckets)
            .map((b) => ({
                ...b,
                // Clicks-to-submission rate. Capped at 100 so a page with
                // more submissions than clicks (rare but possible when the
                // form lives on a page that doesn't have phone/WA CTAs)
                // doesn't render >100% — display logic just shows "—".
                conversionRate: b.clicks > 0 ? (b.submissions / b.clicks) : null,
            }))
            // Sort: most submissions first, then most clicks. Pages with
            // ZERO submissions sort to the bottom so the card always
            // leads with actionable info.
            .sort((a, b) => {
                if (b.submissions !== a.submissions) return b.submissions - a.submissions;
                return b.clicks - a.clicks;
            });

        const kpis = computeClickTrackingKpis(allEvents, formSubmissionsByPage);

        // Partial-fail warnings — surfaced via error field when data is still usable
        const warnings = [];
        if (phoneOk && !waOk) warnings.push("Couldn't load WhatsApp events — phone data shown.");
        else if (!phoneOk && waOk) warnings.push("Couldn't load phone events — WhatsApp data shown.");
        if (!leadsOk) warnings.push('Form submission counts unavailable — click data shown.');

        const partialError = warnings.length > 0 ? warnings.join(' ') : null;

        dispatch(clickTrackingSuccess({
            events: allEvents,
            kpis,
            _fetchKey: fetchKey,
            formSubmissionsByPage,
        }));

        // Non-blocking partial warning stored after success so components see data + warning
        if (partialError) {
            dispatch(clickTrackingError(partialError));
        }
    };
}

export default slice.reducer;
